import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  CompleteStripeConnectedAccountOnboardingInput,
  PaymentControlPlaneUpdateInput,
  SaveRegistrationDraftInput,
  StartStripeConnectedAccountOnboardingInput,
  SubmitRegistrationInput,
} from "@triedge/domain-core";
import {
  domainRouterClient,
  type DomainRouterMutationArgs,
  type DomainRouterMutationData,
  type DomainRouterMutationName,
  type DomainRouterQueryArgs,
  type DomainRouterQueryData,
  type DomainRouterQueryName,
} from "../api/domainRouterClient";
import { toDomainRouterApiError } from "./domainRouterError";
import {
  loadTuitionDashboardSnapshot,
  type TuitionDashboardSnapshot,
} from "../internal-owned/tuition/shared/tuitionData";
import { domainRouterUniversalApi } from "./domainRouterUniversalApi";
import { YSTNJ_DOMAIN_ROUTER_CACHE_RETENTION_SECONDS } from "./domainRouterCachePolicy";

type ApiError = {
  message: string;
};

type Primitive = string | number | boolean | null;
type CacheKeyValue =
  | Primitive
  | CacheKeyValue[]
  | { [key: string]: CacheKeyValue };
type DomainRecord = Record<string, unknown>;
type ConnectionLike<TItem> =
  | {
      items?: readonly TItem[] | TItem[] | null;
      totalCount?: number | null;
      nextToken?: string | null;
    }
  | null
  | undefined;
type Connection<TItem> = {
  items: TItem[];
  totalCount: number;
  nextToken: string | null;
};
type ConnectionItem<TConnection> = TConnection extends {
  items?: readonly (infer Item)[] | (infer Item)[] | null;
}
  ? NonNullable<Item>
  : never;
type ArrayItem<TArray> = TArray extends readonly (infer Item)[] | (infer Item)[]
  ? NonNullable<Item>
  : never;
type ObjectLike<T> =
  T extends Record<string, unknown> ? T : Record<string, unknown>;

export type AdmissionsApplication = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listApplications">>
>;
export type AdmissionsApplicationConnection = Connection<AdmissionsApplication>;
export type AdmissionsDocument = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"getApplicationDocuments">>
>;
export type AdmissionsDocumentConnection = Connection<AdmissionsDocument>;
export type AdmissionsTimelineEvent = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listTimelineEvents">>
>;
export type AdmissionsTimelineConnection = Connection<AdmissionsTimelineEvent>;
export type ParentAdmissionsDraftEnvelope =
  DomainRouterQueryData<"getParentAdmissionsDraft">;
export type ParentAdmissionsDraft = NonNullable<
  ParentAdmissionsDraftEnvelope["draft"]
>;

export type FinancialAidReviewListItem = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listFinancialAidApplicationsForReview">>
>;
export type FinancialAidReviewListResponse =
  Connection<FinancialAidReviewListItem>;
export type FinancialAidReviewDetail =
  DomainRouterQueryData<"getFinancialAidApplicationForReview">;
export type FinancialAidStatusHistoryResponse =
  DomainRouterQueryData<"getFinancialAidStatusHistory">;

export type ModuleConfigResponse = DomainRouterQueryData<"getModuleConfig">;
export type StaffUserListResponse = DomainRouterQueryData<"listStaffUsers">;
export type ModuleEnablementAuditEventsResponse =
  DomainRouterQueryData<"listModuleEnablementAuditEvents">;
export type ImpersonationSessionResponse =
  DomainRouterQueryData<"getImpersonationSession">;
export type ViewAsContextResponse = DomainRouterQueryData<"getViewAsContext">;
export type TenantSchoolsResponse = DomainRouterQueryData<"listTenantSchools">;
export type ActiveSchoolContextResponse =
  DomainRouterQueryData<"getActiveSchoolContext">;
export type PaymentControlPlaneResponse =
  DomainRouterQueryData<"getPaymentControlPlane">;
export type PaymentControlPlaneReadinessResponse =
  DomainRouterQueryData<"getPaymentControlPlaneReadiness">;
export type PaymentManagementContextResponse =
  DomainRouterQueryData<"getMyPaymentManagementContext">;
export type PaymentMirrorLedgerListResponse =
  DomainRouterQueryData<"listPaymentMirrorLedgerEntries">;
export type StripeConnectedAccountOnboardingResponse =
  DomainRouterMutationData<"startStripeConnectedAccountOnboarding">;
export type CompleteStripeConnectedAccountOnboardingResponse =
  DomainRouterMutationData<"completeStripeConnectedAccountOnboarding">;
export type FinancialAidHistoricalImportTemplateResponse =
  DomainRouterQueryData<"getFinancialAidHistoricalImportTemplate">;
export type FinancialAidHistoricalImportStatusResponse =
  DomainRouterQueryData<"getFinancialAidHistoricalImportStatus">;
export type FinancialAidHistoricalImportUploadResponse =
  DomainRouterMutationData<"requestFinancialAidHistoricalImportUpload">;
export type AssistantSpreadsheetBridgeResponse = DomainRecord;
export type AssistantWorkflowTemplateListResponse =
  DomainRouterQueryData<"listAssistantWorkflowTemplates">;
export type AssistantWorkflowDefinitionResponse =
  DomainRouterQueryData<"getAssistantWorkflow">;
export type AssistantWorkflowExecutionListResponse =
  DomainRouterQueryData<"listAssistantWorkflowExecutions">;
export type AssistantWorkflowMutationResponse =
  DomainRouterMutationData<"upsertAssistantWorkflowDraft">;

export type PortalStudentProfile = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listPortalStudentProfiles">>
>;
export type PortalStudentProfileListResponse = Connection<PortalStudentProfile>;
export type PortalDocument = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listPortalDocuments">>
>;
export type PortalDocumentListResponse = Connection<PortalDocument>;
export type PortalCalendarEvent = ObjectLike<
  ArrayItem<DomainRouterQueryData<"listPortalCalendarEvents">>
>;
export type PortalTransaction = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listPortalTransactions">>
>;
export type PortalTransactionListResponse = Connection<PortalTransaction>;
export type PortalResource = ObjectLike<
  ArrayItem<DomainRouterQueryData<"listPortalResources">>
>;
export type MessageThread = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listMessageThreads">>
>;
export type PortalThreadListResponse = Connection<MessageThread>;
export type FacultyMember = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listFaculty">>
>;
export type FacultyConnection = Connection<FacultyMember>;
export type FamilyRecord = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listFamilies">>
>;
export type FamilyConnection = Connection<FamilyRecord>;
export type FamilyBalanceResponse = DomainRouterQueryData<"getFamilyBalance">;
export type FamilyAccountTransaction = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listFamilyAccountTransactions">>
>;
export type FamilyAccountTransactionConnection =
  Connection<FamilyAccountTransaction>;
export type FamilyPaymentMethod = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listFamilyPaymentMethods">>
>;
export type FamilyPaymentMethodConnection = Connection<FamilyPaymentMethod>;
export type SchoolTuitionDashboardTreeResponse =
  DomainRouterQueryData<"getSchoolTuitionDashboardTree">;
export type SchoolTuitionFamilyLedgerTreeResponse =
  DomainRouterQueryData<"getSchoolTuitionFamilyLedgerTree">;
export type AdHocInvoiceResponse = DomainRouterQueryData<"getAdHocInvoice">;
export type AdHocInvoiceListResponse =
  DomainRouterQueryData<"listAdHocInvoices">;
export type BulkChargeJobResponse = DomainRouterQueryData<"getBulkChargeJob">;
export type BulkChargeSegmentPreviewResponse =
  DomainRouterQueryData<"previewBulkChargeSegment">;
export type RecurringFeeDefinitionResponse =
  DomainRouterQueryData<"getRecurringFeeDefinition">;
export type RecurringFeeDefinitionListResponse =
  DomainRouterQueryData<"listRecurringFeeDefinitions">;
export type RecurringFeeAssignmentListResponse =
  DomainRouterQueryData<"listRecurringFeeAssignments">;

export type PortalThreadDetail = {
  thread: DomainRecord;
  messages: DomainRecord[];
};

export type NotificationItem = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listNotifications">>
>;
export type NotificationConnection = Connection<NotificationItem>;

export type NotificationPreferenceItem = ObjectLike<
  ConnectionItem<DomainRouterQueryData<"listNotificationPreferences">>
>;
export type NotificationPreferenceConnection =
  Connection<NotificationPreferenceItem>;

type AdmissionsListArgs = {
  limit?: number;
  nextToken?: string;
  filter?: DomainRecord;
};

type FacultyListArgs = {
  limit?: number;
  nextToken?: string;
  filter?: DomainRecord;
};

type FamilyListArgs = {
  limit?: number;
  offset?: number;
};

type FamilySearchArgs = {
  query: string;
  limit?: number;
};

type FamilyBalanceQueryArgs = {
  familyId: string;
  academicYear: string;
};

type FamilyAccountTransactionsArgs = {
  familyId: string;
  academicYear?: string;
  limit?: number;
  nextToken?: string | null;
};

type FamilyPaymentMethodsArgs = {
  familyId: string;
  limit?: number;
  nextToken?: string | null;
};

type SchoolTuitionDashboardTreeArgs = {
  academicYear: string;
  limit?: number;
  offset?: number;
  searchQuery?: string;
};

type SchoolTuitionFamilyLedgerTreeArgs = {
  academicYear: string;
  familyId: string;
};

type AdHocInvoiceArgs = {
  invoiceId: string;
};

type AdHocInvoiceListArgs = {
  tuitionAccountId: string;
  limit?: number;
  nextToken?: string | null;
};

type BulkChargeJobArgs = {
  bulkChargeJobId: string;
};

type PreviewBulkChargeSegmentArgs = {
  grades?: string[];
  divisions?: string[];
  gender?: "ALL" | "BOYS" | "GIRLS";
  rateCategories?: string[];
};

type RecurringFeeDefinitionArgs = {
  recurringFeeId: string;
};

type RecurringFeeDefinitionListArgs = {
  schoolId?: string;
  isActive?: boolean;
};

type RecurringFeeAssignmentListArgs = {
  tuitionAccountId?: string;
  studentId?: string;
  recurringFeeId?: string;
  isActive?: boolean;
  limit?: number;
  nextToken?: string | null;
};

type ReverseTuitionPaymentInput =
  DomainRouterMutationArgs<"reverseTuitionPayment">["input"];
type ArchiveAdHocTuitionChargeInput =
  DomainRouterMutationArgs<"archiveAdHocTuitionCharge">["input"];
type IssueTuitionRefundInput =
  DomainRouterMutationArgs<"issueTuitionRefund">["input"];
type CreateAdHocChargeInput =
  DomainRouterMutationArgs<"createAdHocCharge">["input"];
type CreateBulkChargeInput =
  DomainRouterMutationArgs<"createBulkCharge">["input"];
type CreateRecurringFeeDefinitionInput =
  DomainRouterMutationArgs<"createRecurringFeeDefinition">["input"];
type UpdateRecurringFeeDefinitionInput =
  DomainRouterMutationArgs<"updateRecurringFeeDefinition">["input"];
type DeleteRecurringFeeDefinitionInput =
  DomainRouterMutationArgs<"deleteRecurringFeeDefinition">["input"];
type AssignRecurringFeeInput =
  DomainRouterMutationArgs<"assignRecurringFee">["input"];
type UnassignRecurringFeeInput =
  DomainRouterMutationArgs<"unassignRecurringFee">["input"];
type BulkAssignRecurringFeeInput =
  DomainRouterMutationArgs<"bulkAssignRecurringFee">["input"];

type SubmitAdmissionsApplicationInput =
  DomainRouterMutationArgs<"submitApplication">;
type ParentAdmissionsDraftQueryArgs =
  DomainRouterQueryArgs<"getParentAdmissionsDraft">;
type UpsertParentAdmissionsDraftInput =
  DomainRouterMutationArgs<"upsertParentAdmissionsDraft">;
type DeleteParentAdmissionsDraftInput =
  DomainRouterMutationArgs<"deleteParentAdmissionsDraft">;
type AppendEnrollmentApplicationInput = {
  applicationId: string;
  updates: DomainRecord;
};
type UpdateAdmissionsApplicationInput = {
  applicationId: string;
  updates: DomainRecord;
};

type ApplicantApplicationsArgs = {
  applicantId: string;
  limit?: number;
  nextToken?: string;
};

type AdmissionsDocumentsArgs = {
  applicationId: string;
  limit?: number;
  nextToken?: string;
};

type AdmissionsTimelineArgs = {
  applicationId: string;
  limit?: number;
  nextToken?: string;
};

type FinancialAidDashboardArgs = {
  status?: string | string[];
  hasFlags?: boolean;
  academicYear?: string;
  assignedTo?: string;
  limit?: number;
  nextToken?: string;
  scope?: "deadline_extension_candidates";
};

type AssistantWorkflowTemplatesArgs =
  DomainRouterQueryArgs<"listAssistantWorkflowTemplates">;
type AssistantWorkflowArgs = DomainRouterQueryArgs<"getAssistantWorkflow">;
type AssistantWorkflowExecutionsArgs =
  DomainRouterQueryArgs<"listAssistantWorkflowExecutions">;
type UpsertAssistantWorkflowDraftArgs =
  DomainRouterMutationArgs<"upsertAssistantWorkflowDraft">;
type SubmitAssistantWorkflowForApprovalArgs =
  DomainRouterMutationArgs<"submitAssistantWorkflowForApproval">;
type ReviewAssistantWorkflowArgs =
  DomainRouterMutationArgs<"reviewAssistantWorkflow">;
type MutateAssistantRunArgs = DomainRouterMutationArgs<"mutateAssistantRun">;

type ModuleConfigQueryArgs = {
  orgId?: string;
  moduleKey?: string;
};

type PaymentControlPlaneQueryArgs = {
  tenantId?: string;
};

type PaymentManagementContextQueryArgs = {
  tenantId?: string;
  moduleId?: string;
};

type PaymentMirrorLedgerQueryArgs = {
  tenantId?: string;
  moduleId?: string;
  limit?: number;
  nextToken?: string;
};

type UpdateModuleConfigInput = {
  orgId?: string;
  moduleKey?: string;
  patch: DomainRecord;
  integrationStatus?: DomainRecord;
};

type UpdatePaymentControlPlaneInput = PaymentControlPlaneUpdateInput;
type StartStripeConnectedAccountOnboardingMutationInput =
  StartStripeConnectedAccountOnboardingInput;
type CompleteStripeConnectedAccountOnboardingMutationInput =
  CompleteStripeConnectedAccountOnboardingInput;

type FinancialAidHistoricalImportTemplateQueryArgs = {
  orgId?: string;
  moduleKey?: string;
};

type FinancialAidHistoricalImportStatusQueryArgs = {
  orgId?: string;
  moduleKey?: string;
  importId?: string;
};

type FinancialAidHistoricalImportUploadInput = {
  orgId?: string;
  moduleKey?: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
};

type StartFinancialAidHistoricalImportInput = {
  orgId?: string;
  moduleKey?: string;
  importId?: string;
};

type AssistantSpreadsheetBridgeStatusQueryArgs = {
  tenantId?: string;
  userSub?: string;
};

type AssistantSpreadsheetWorkbookDraftStateQueryArgs = {
  tenantId?: string;
  userSub?: string;
  workbookArtifactPath: string;
};

type AssistantSpreadsheetBridgeStartAuthInput = {
  tenantId?: string;
  userSub?: string;
  redirectUri?: string;
  requestedScopes?: string[];
};

type AssistantSpreadsheetBridgeOAuthCallbackInput = {
  state: string;
  code: string;
};

type AssistantSpreadsheetBridgeExportInput = {
  tenantId?: string;
  userSub?: string;
  destinationFolderId?: string;
  destinationFileId?: string;
  fileName: string;
  workbookArtifactPath?: string;
  artifactDownloadUrl?: string;
  mimeType?: string;
};

type AssistantSpreadsheetBridgeDisconnectInput = {
  tenantId?: string;
  userSub?: string;
};

type AssistantSpreadsheetWorkbookDraftSaveInput = {
  tenantId?: string;
  userSub?: string;
  workbookArtifactPath: string;
  fileName: string;
  workbook: DomainRecord;
};

type AssistantSpreadsheetWorkbookDraftRestoreInput = {
  tenantId?: string;
  userSub?: string;
  workbookArtifactPath: string;
  versionId: string;
};

type AssistantSpreadsheetExportPresetSaveInput = {
  tenantId?: string;
  userSub?: string;
  presetId?: string;
  label: string;
  destinationType: "new_sheet" | "existing_sheet";
  destinationFileId?: string;
  destinationFolderId?: string;
  spreadsheetUrl?: string | null;
  fileNameTemplate?: string | null;
};

type AssistantSpreadsheetExportPresetDeleteInput = {
  tenantId?: string;
  userSub?: string;
  presetId: string;
};

type PortalProfileQueryArgs = {
  applicationId?: string;
  applicantId?: string;
  familyId?: string;
};

type PortalCalendarQueryArgs = PortalProfileQueryArgs & {
  eventTypes?: string[];
  startDate?: string;
  endDate?: string;
};

type PortalResourcesQueryArgs = {
  orgId?: string;
  moduleKey?: string;
};

type PortalMessageThreadQueryArgs = {
  applicationId?: string;
  familyId?: string;
  participantId?: string;
};

export type PortalNotificationQueryArgs = {
  actorId?: string;
  familyId?: string;
  role?: string;
  includeRead?: boolean;
  limit?: number;
  modules?: string[];
  categories?: string[];
  nextToken?: string | null;
};

type NotificationPreferencesQueryArgs = {
  actorId?: string;
  role?: string;
};

type SendMessageInput = {
  threadId?: string;
  familyId?: string;
  applicationId?: string;
  participantIds?: string[];
  senderId?: string;
  senderRole?: string;
  subject?: string;
  body: string;
};

type MarkMessageReadInput = {
  threadId: string;
  messageId: string;
  readAt?: number;
};

type MarkNotificationReadInput = {
  id: string;
  actorId?: string;
  readAt?: number;
};

type MarkAllNotificationsReadInput = {
  actorId?: string;
  familyId?: string;
  modules?: string[];
  categories?: string[];
  readAt?: number;
};

type UpdateNotificationPreferencesInput = {
  actorId?: string;
  role?: string;
  moduleFilters?: string[];
  categoryFilters?: string[];
  muted?: boolean;
  digestFrequency?: string;
};

type AdmissionsNoteInput = {
  noteType: string;
  content: string;
  isImportant?: boolean;
  authorName?: string | null;
  visibility?: string[];
};

type AddAdmissionNoteInput = {
  applicationId: string;
  note: AdmissionsNoteInput;
};

type UpdateAdmissionsNoteInput = {
  applicationId: string;
  noteId: string;
  content?: string;
  isImportant?: boolean;
  visibility?: string[];
};

type DeleteAdmissionsNoteInput = {
  applicationId: string;
  noteId: string;
};

type AddAdmissionsDocumentInput = {
  applicationId: string;
  document: {
    resourceId: string;
    category: string;
    uploadedBy?: string | null;
    fileName?: string;
    contentType?: string;
    sizeBytes?: number;
    childApplicationIds?: string[];
    storageProvider?: "s3";
    storageKey?: string;
    storageBucket?: string;
    storageRegion?: string;
  };
};

type UpdateAdmissionsStatusInput = {
  applicationId: string;
  status: string;
};

type ScheduleAdmissionsInterviewInput = {
  applicationId: string;
  interviewDate: number;
};

type RequestAdmissionsInterviewScheduleInput = {
  applicationId: string;
  preferredSlots: number[];
  notes?: string;
};

type ScheduleAdmissionsVisitInput = {
  applicationId: string;
  visitDate: number;
};

type SendAdmissionsMessageInput = {
  applicationId: string;
  subject: string;
  message: string;
};

type UpdateAdmissionsChildApplicationInput = {
  applicationId: string;
  childApplicationId: string;
  status?: string;
  assignedTo?: string | null;
  assigneeName?: string | null;
  enrollmentDeadline?: number | null;
  childInfo?: DomainRecord;
  healthInfo?: DomainRecord | null;
  gradeApplyingFor?: string;
  schoolDivision?: string;
  previousSchool?: string | null;
};

type UpsertEnrollmentStepDataInput = {
  childApplicationId: string;
  step:
    | "step1AcceptanceLetter"
    | "step4TuitionContract"
    | "step3B6TForm"
    | "step5Payment";
  data: DomainRecord;
};

type UpdateEnrollmentProgressInput = {
  childApplicationId: string;
  step:
    | "step1AcceptanceLetter"
    | "step2RegistrationForm"
    | "step3B6TForm"
    | "step4TuitionContract"
    | "step5Payment"
    | "step6ImmunizationRecords";
  completed: boolean;
};

type UpdateMedicalRecordInput = {
  childApplicationId: string;
  medical: DomainRecord;
};

const DEFAULT_MODULE_KEY = "staff_settings";

const asError = (error: unknown): ApiError => toDomainRouterApiError(error);

const toText = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

const normalizeStringArray = (
  value: string[] | undefined,
): string[] | undefined => {
  if (!value?.length) {
    return undefined;
  }
  const normalized = value
    .map((entry) => String(entry ?? "").trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right));
  return normalized.length ? normalized : undefined;
};

const compact = <T extends DomainRecord>(value: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(value).filter(([, entry]) => {
      if (entry == null) {
        return false;
      }
      if (typeof entry === "string") {
        return Boolean(entry.trim());
      }
      if (Array.isArray(entry)) {
        return entry.length > 0;
      }
      return true;
    }),
  ) as Partial<T>;

const normalizePortalProfileArgs = (
  args: PortalProfileQueryArgs | void = {},
): PortalProfileQueryArgs => {
  const source = args ?? {};
  return compact({
    applicationId: toText(source.applicationId),
    applicantId: toText(source.applicantId),
    familyId: toText(source.familyId),
  });
};

const normalizePortalCalendarArgs = (
  args: PortalCalendarQueryArgs | void = {},
): PortalCalendarQueryArgs => {
  const source = args ?? {};
  const eventTypes = normalizeStringArray(source.eventTypes);
  return compact({
    applicationId: toText(source.applicationId),
    applicantId: toText(source.applicantId),
    familyId: toText(source.familyId),
    startDate: toText(source.startDate),
    endDate: toText(source.endDate),
    eventTypes,
  });
};

const normalizePortalResourcesArgs = (
  args: PortalResourcesQueryArgs | void = {},
): PortalResourcesQueryArgs =>
  compact({
    orgId: toText((args ?? {}).orgId),
    moduleKey: toText((args ?? {}).moduleKey),
  });

const normalizePortalThreadArgs = (
  args: PortalMessageThreadQueryArgs | void = {},
): PortalMessageThreadQueryArgs =>
  compact({
    applicationId: toText((args ?? {}).applicationId),
    familyId: toText((args ?? {}).familyId),
    participantId: toText((args ?? {}).participantId),
  });

const normalizePortalNotificationArgs = (
  args: PortalNotificationQueryArgs | void = {},
): PortalNotificationQueryArgs => {
  const source = args ?? {};
  const normalized: PortalNotificationQueryArgs = {};
  const actorId = toText(source.actorId);
  const familyId = toText(source.familyId);
  const role = toText(source.role);
  const nextToken = toText(source.nextToken ?? undefined);
  const modules = normalizeStringArray(source.modules);
  const categories = normalizeStringArray(source.categories);

  if (actorId) normalized.actorId = actorId;
  if (familyId) normalized.familyId = familyId;
  if (role) normalized.role = role;
  if (typeof source.includeRead === "boolean")
    normalized.includeRead = source.includeRead;
  if (typeof source.limit === "number" && Number.isFinite(source.limit))
    normalized.limit = source.limit;
  if (modules) normalized.modules = modules;
  if (categories) normalized.categories = categories;
  if (nextToken) normalized.nextToken = nextToken;

  return normalized;
};

const stripNotificationActorId = (
  args: PortalNotificationQueryArgs | void = {},
): PortalNotificationQueryArgs => {
  const { actorId: _actorId, ...rest } = normalizePortalNotificationArgs(args);
  return rest;
};

const normalizeNotificationPreferencesArgs = (
  args: NotificationPreferencesQueryArgs | void = {},
): NotificationPreferencesQueryArgs =>
  compact({
    actorId: toText((args ?? {}).actorId),
    role: toText((args ?? {}).role),
  });

const normalizeSettingsArgs = (args: ModuleConfigQueryArgs | void = {}) => ({
  orgId: toText((args ?? {}).orgId) ?? "default",
  moduleKey: toText((args ?? {}).moduleKey) ?? DEFAULT_MODULE_KEY,
});

const normalizePaymentControlPlaneArgs = (
  args: PaymentControlPlaneQueryArgs | void = {},
): PaymentControlPlaneQueryArgs =>
  compact({
    tenantId: toText((args ?? {}).tenantId),
  });

const normalizePaymentManagementContextArgs = (
  args: PaymentManagementContextQueryArgs | void = {},
): PaymentManagementContextQueryArgs =>
  compact({
    tenantId: toText((args ?? {}).tenantId),
    moduleId: toText((args ?? {}).moduleId),
  });

const normalizePaymentMirrorLedgerArgs = (
  args: PaymentMirrorLedgerQueryArgs | void = {},
): PaymentMirrorLedgerQueryArgs => {
  const source = args ?? {};
  const normalized: PaymentMirrorLedgerQueryArgs = compact({
    tenantId: toText(source.tenantId),
    moduleId: toText(source.moduleId),
    nextToken: toText(source.nextToken),
  });
  if (typeof source.limit === "number" && Number.isFinite(source.limit)) {
    normalized.limit = source.limit;
  }
  return normalized;
};

const normalizeAssistantSpreadsheetBridgeArgs = (
  args:
    | AssistantSpreadsheetBridgeStatusQueryArgs
    | AssistantSpreadsheetBridgeDisconnectInput
    | void = {},
) =>
  compact({
    tenantId: toText((args ?? {}).tenantId),
    userSub: toText((args ?? {}).userSub),
  });

const normalizeAssistantSpreadsheetDraftStateArgs = (
  args: AssistantSpreadsheetWorkbookDraftStateQueryArgs,
) =>
  compact({
    ...normalizeAssistantSpreadsheetBridgeArgs(args),
    workbookArtifactPath: toText(args.workbookArtifactPath),
  });

const normalizeHistoricalImportStatusArgs = (
  args: FinancialAidHistoricalImportStatusQueryArgs | void = {},
) => ({
  ...normalizeSettingsArgs(args),
  importId: toText((args ?? {}).importId),
});

const toSettingsQueryInput = (args: ModuleConfigQueryArgs | void = {}) => {
  const normalized = normalizeSettingsArgs(args);
  if (normalized.orgId === "default") {
    return { moduleKey: normalized.moduleKey };
  }
  return { orgId: normalized.orgId, moduleKey: normalized.moduleKey };
};

const toHistoricalImportStatusInput = (
  args: FinancialAidHistoricalImportStatusQueryArgs | void = {},
) => {
  const normalized = normalizeHistoricalImportStatusArgs(args);
  const base: Record<string, unknown> = {
    orgId: normalized.orgId ?? "default",
    moduleKey: normalized.moduleKey,
  };
  if (normalized.importId) {
    base.importId = normalized.importId;
  }
  return base;
};

const toCacheKeyValue = (value: unknown): CacheKeyValue => {
  if (value == null) {
    return null;
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (Array.isArray(value)) {
    const normalizedItems = value.map((entry) => toCacheKeyValue(entry));
    const allPrimitive = normalizedItems.every(
      (entry) =>
        entry == null ||
        typeof entry === "string" ||
        typeof entry === "number" ||
        typeof entry === "boolean",
    );
    if (allPrimitive) {
      return [...normalizedItems].sort((left, right) =>
        String(left).localeCompare(String(right)),
      );
    }
    return normalizedItems;
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as DomainRecord)
      .filter(
        ([, entry]) =>
          entry != null && !(typeof entry === "string" && entry.trim() === ""),
      )
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, toCacheKeyValue(entry)] as const);
    return Object.fromEntries(entries);
  }
  return String(value);
};

const buildCacheKey = (scope: string, value: unknown): string =>
  `${scope}:${JSON.stringify(toCacheKeyValue(value))}`;

const toConnection = <TItem>(
  value: ConnectionLike<TItem>,
): Connection<TItem> => ({
  items: Array.isArray(value?.items) ? [...value.items] : [],
  totalCount: Number(value?.totalCount ?? 0),
  nextToken: value?.nextToken == null ? null : String(value.nextToken),
});

const toArray = <TItem>(
  value: readonly TItem[] | TItem[] | null | undefined,
): TItem[] => (Array.isArray(value) ? [...value] : []);

const asObject = <TRecord extends DomainRecord>(
  value: unknown,
  defaultValue: TRecord,
): TRecord => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return defaultValue;
  }
  return value as TRecord;
};

const parsePayloadRecord = (value: unknown): DomainRecord => {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return asObject(parsed, {});
    } catch {
      return {};
    }
  }
  return asObject(value, {});
};

const applyDomainPatch = (
  base: DomainRecord,
  patch: DomainRecord,
): DomainRecord => {
  const next: DomainRecord = { ...base };
  for (const [key, patchValue] of Object.entries(patch)) {
    if (Array.isArray(patchValue)) {
      next[key] =
        typeof structuredClone === "function"
          ? structuredClone(patchValue)
          : JSON.parse(JSON.stringify(patchValue));
      continue;
    }
    if (patchValue && typeof patchValue === "object") {
      const baseValue = asObject(next[key], {});
      next[key] = applyDomainPatch(baseValue, asObject(patchValue, {}));
      continue;
    }
    next[key] = patchValue;
  }
  return next;
};

const admissionsTimelineTagId = (applicationId: string): string =>
  buildCacheKey("AdmissionsTimeline", { applicationId: applicationId.trim() });

const applicantApplicationsTagId = (applicantId: string): string =>
  buildCacheKey("ApplicantApplications", { applicantId: applicantId.trim() });

const settingsConfigTagId = (args: ModuleConfigQueryArgs | void): string =>
  buildCacheKey("SettingsConfig", normalizeSettingsArgs(args));

const paymentControlPlaneTagId = (
  args: PaymentControlPlaneQueryArgs | void,
): string =>
  buildCacheKey("PaymentControlPlane", normalizePaymentControlPlaneArgs(args));

const paymentReadinessTagId = (
  args: PaymentControlPlaneQueryArgs | void,
): string =>
  buildCacheKey("PaymentReadiness", normalizePaymentControlPlaneArgs(args));

const paymentManagementContextTagId = (
  args: PaymentManagementContextQueryArgs | void,
): string =>
  buildCacheKey(
    "PaymentManagementContext",
    normalizePaymentManagementContextArgs(args),
  );

const paymentMirrorLedgerTagId = (
  args: PaymentMirrorLedgerQueryArgs | void,
): string =>
  buildCacheKey("PaymentMirrorLedger", normalizePaymentMirrorLedgerArgs(args));

const portalArgsTagId = (scope: string, args: unknown): string =>
  buildCacheKey(scope, args);

const portalTransactionsTagId = (
  familyId: string,
  academicYear?: string,
): string =>
  buildCacheKey("PortalTransactions", {
    familyId: familyId.trim(),
    academicYear: toText(academicYear),
  });

const messageThreadsListTagId = (args: {
  familyId?: string;
  participantId?: string;
  applicationId?: string;
}): string =>
  buildCacheKey("MessageThreads", {
    familyId: toText(args.familyId),
    participantId: toText(args.participantId),
    applicationId: toText(args.applicationId),
  });

const notificationsListTagId = (args: {
  actorId?: string;
  familyId?: string;
  modules?: string[];
  categories?: string[];
  includeRead?: boolean;
}): string =>
  buildCacheKey("Notifications", {
    actorId: toText(args.actorId),
    familyId: toText(args.familyId),
    modules: normalizeStringArray(args.modules),
    categories: normalizeStringArray(args.categories),
    includeRead: args.includeRead,
  });

const notificationPreferencesTagId = (args: {
  actorId?: string;
  role?: string;
}): string =>
  buildCacheKey("NotificationPreferences", {
    actorId: toText(args.actorId),
    role: toText(args.role),
  });

const assistantSpreadsheetBridgeTagId = (
  args: {
    tenantId?: string;
    userSub?: string;
  } | void = {},
): string =>
  buildCacheKey("AssistantSpreadsheetBridge", {
    tenantId: toText(args?.tenantId),
    userSub: toText(args?.userSub),
  });

export const domainRouterApi = createApi({
  reducerPath: "domainRouterApi",
  baseQuery: fakeBaseQuery<ApiError>(),
  keepUnusedDataFor: YSTNJ_DOMAIN_ROUTER_CACHE_RETENTION_SECONDS,
  serializeQueryArgs: ({ endpointName, queryArgs }) =>
    buildCacheKey(endpointName, queryArgs),
  tagTypes: [
    "AdmissionsApplications",
    "AdmissionsApplication",
    "AdmissionsDocuments",
    "AdmissionsTimeline",
    "ParentAdmissionsDraft",
    "FinancialAidReviews",
    "FinancialAidReview",
    "SettingsConfig",
    "PaymentControlPlane",
    "PaymentReadiness",
    "PortalProfiles",
    "PortalDocuments",
    "PortalCalendar",
    "PortalTransactions",
    "PortalResources",
    "MessageThreads",
    "MessageThread",
    "Notifications",
    "NotificationPreferences",
    "Faculty",
    "AssistantSpreadsheetBridge",
    "DonationsCampaign",
    "DonationsCalendar",
    "DonationsFamilyHistory",
    "DonationsDedications",
    "DonationsLedger",
    "DonationsTenantConfig",
    "CampsParentCatalog",
    "CampsParentRegistration",
    "CampsParentWaitlist",
    "CampsParentRegistrations",
    "CampsSchoolDashboard",
    "CampsProgram",
    "CampsProgramEditor",
    "CampsCrosswalkQueue",
    "CampsCrosswalkDetail",
    "StaffAdministrators",
    "StaffUsers",
    "StaffCodes",
    "PendingStaffRequests",
    "ApprovedDomains",
    "ModuleGovernanceAudit",
    "AdminImpersonationSession",
    "AdminViewAsContext",
    "TenantSchools",
    "ActiveSchoolContext",
    "TuitionDashboard",
    "TuitionInvoices",
    "TuitionBulkJobs",
    "TuitionRecurringFees",
    "TuitionRecurringAssignments",
  ],
  endpoints: (builder) => ({
    listAdmissionsApplications: builder.query<
      AdmissionsApplicationConnection,
      AdmissionsListArgs | void
    >({
      queryFn: async (args) => {
        const resolvedArgs = args ?? {};
        try {
          const data = await domainRouterClient.query("listApplications", {
            limit: resolvedArgs.limit,
            nextToken: resolvedArgs.nextToken,
            filter: resolvedArgs.filter,
          });
          return {
            data: toConnection<AdmissionsApplication>(
              data as ConnectionLike<AdmissionsApplication>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result) => [
        { type: "AdmissionsApplications", id: "LIST" },
        ...(result?.items ?? []).map((item) => ({
          type: "AdmissionsApplication" as const,
          id: String(item.id ?? ""),
        })),
      ],
    }),

    listFacultyMembers: builder.query<
      FacultyConnection,
      FacultyListArgs | void
    >({
      queryFn: async (args) => {
        const resolvedArgs = args ?? {};
        try {
          const requestedFilter = resolvedArgs.filter ?? {};
          const hasRequiredFilter = [
            "status",
            "orgId",
            "divisionId",
            "userId",
          ].some((key) => {
            const candidate = requestedFilter[key];
            if (Array.isArray(candidate)) return candidate.length > 0;
            if (typeof candidate === "string")
              return candidate.trim().length > 0;
            return candidate !== undefined && candidate !== null;
          });
          const filter: DomainRecord = {
            ...requestedFilter,
            ...(hasRequiredFilter ? {} : { status: "active" }),
            limit: resolvedArgs.limit ?? 200,
          };
          if (resolvedArgs.nextToken) {
            filter.nextToken = resolvedArgs.nextToken;
          }
          const data = await domainRouterClient.query("listFaculty", {
            filter,
          });
          return {
            data: toConnection<FacultyMember>(
              data as ConnectionLike<FacultyMember>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result) => [
        { type: "Faculty", id: "LIST" },
        ...(result?.items ?? []).map((item) => ({
          type: "Faculty" as const,
          id: String(item.id ?? ""),
        })),
      ],
    }),

    listFamilies: builder.query<FamilyConnection, FamilyListArgs | void>({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("listFamilies", {
            input: JSON.stringify(
              compact({
                limit: args?.limit,
                offset: args?.offset,
              }),
            ),
          });
          return {
            data: toConnection<FamilyRecord>(
              data as ConnectionLike<FamilyRecord>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    searchFamilies: builder.query<FamilyConnection, FamilySearchArgs>({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("searchFamilies", {
          query: toText(queryArgs.query),
          limit: queryArgs.limit,
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("searchFamilies", {
            input: JSON.stringify(
              compact({
                query: toText(args.query),
                limit: args.limit,
              }),
            ),
          });
          return {
            data: toConnection<FamilyRecord>(
              data as ConnectionLike<FamilyRecord>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    getFamilyBalance: builder.query<
      FamilyBalanceResponse,
      FamilyBalanceQueryArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getFamilyBalance", {
          familyId: toText(queryArgs.familyId),
          academicYear: toText(queryArgs.academicYear),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getFamilyBalance", {
            familyId: toText(args.familyId) ?? "",
            academicYear: toText(args.academicYear) ?? "",
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    listFamilyAccountTransactions: builder.query<
      FamilyAccountTransactionConnection,
      FamilyAccountTransactionsArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listFamilyAccountTransactions", {
          familyId: toText(queryArgs.familyId),
          academicYear: toText(queryArgs.academicYear),
          limit: queryArgs.limit,
          nextToken: toText(queryArgs.nextToken ?? undefined),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listFamilyAccountTransactions",
            {
              familyId: toText(args.familyId) ?? "",
              academicYear: toText(args.academicYear),
              limit: args.limit,
              nextToken: toText(args.nextToken ?? undefined),
            },
          );
          return {
            data: toConnection<FamilyAccountTransaction>(
              data as ConnectionLike<FamilyAccountTransaction>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    listFamilyPaymentMethods: builder.query<
      FamilyPaymentMethodConnection,
      FamilyPaymentMethodsArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listFamilyPaymentMethods", {
          familyId: toText(queryArgs.familyId),
          limit: queryArgs.limit,
          nextToken: toText(queryArgs.nextToken ?? undefined),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listFamilyPaymentMethods",
            {
              familyId: toText(args.familyId) ?? "",
              limit: args.limit,
              nextToken: toText(args.nextToken ?? undefined),
            },
          );
          return {
            data: toConnection<FamilyPaymentMethod>(
              data as ConnectionLike<FamilyPaymentMethod>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    getSchoolTuitionDashboardTree: builder.query<
      SchoolTuitionDashboardTreeResponse,
      SchoolTuitionDashboardTreeArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getSchoolTuitionDashboardTree", {
          academicYear: toText(queryArgs.academicYear),
          limit: queryArgs.limit,
          offset: queryArgs.offset,
          searchQuery: toText(queryArgs.searchQuery),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getSchoolTuitionDashboardTree",
            {
              academicYear: toText(args.academicYear) ?? "",
              limit: args.limit,
              offset: args.offset,
              searchQuery: toText(args.searchQuery),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "TuitionDashboard", id: "SCHOOL_TREE" }],
    }),

    getSchoolTuitionFamilyLedgerTree: builder.query<
      SchoolTuitionFamilyLedgerTreeResponse,
      SchoolTuitionFamilyLedgerTreeArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getSchoolTuitionFamilyLedgerTree", {
          academicYear: toText(queryArgs.academicYear),
          familyId: toText(queryArgs.familyId),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getSchoolTuitionFamilyLedgerTree",
            {
              academicYear: toText(args.academicYear) ?? "",
              familyId: toText(args.familyId) ?? "",
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        { type: "TuitionDashboard", id: `LEDGER:${args.familyId}` },
      ],
    }),

    getAdHocInvoice: builder.query<AdHocInvoiceResponse, AdHocInvoiceArgs>({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getAdHocInvoice", {
          invoiceId: toText(queryArgs.invoiceId),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getAdHocInvoice", {
            invoiceId: toText(args.invoiceId) ?? "",
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TuitionInvoices", id: `INVOICE:${args.invoiceId}` },
      ],
    }),

    listAdHocInvoices: builder.query<
      Connection<ConnectionItem<AdHocInvoiceListResponse>>,
      AdHocInvoiceListArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listAdHocInvoices", {
          tuitionAccountId: toText(queryArgs.tuitionAccountId),
          limit: queryArgs.limit,
          nextToken: toText(queryArgs.nextToken ?? undefined),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("listAdHocInvoices", {
            tuitionAccountId: toText(args.tuitionAccountId) ?? "",
            limit: args.limit,
            nextToken: toText(args.nextToken ?? undefined),
          });
          return {
            data: toConnection<ConnectionItem<AdHocInvoiceListResponse>>(
              data as ConnectionLike<ConnectionItem<AdHocInvoiceListResponse>>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TuitionInvoices", id: `LIST:${args.tuitionAccountId}` },
        { type: "TuitionDashboard", id: `LEDGER:${args.tuitionAccountId}` },
      ],
    }),

    getBulkChargeJob: builder.query<BulkChargeJobResponse, BulkChargeJobArgs>({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getBulkChargeJob", {
          bulkChargeJobId: toText(queryArgs.bulkChargeJobId),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getBulkChargeJob", {
            bulkChargeJobId: toText(args.bulkChargeJobId) ?? "",
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TuitionBulkJobs", id: `JOB:${args.bulkChargeJobId}` },
      ],
    }),

    getRecurringFeeDefinition: builder.query<
      RecurringFeeDefinitionResponse,
      RecurringFeeDefinitionArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getRecurringFeeDefinition", {
          recurringFeeId: toText(queryArgs.recurringFeeId),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getRecurringFeeDefinition",
            {
              recurringFeeId: toText(args.recurringFeeId) ?? "",
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TuitionRecurringFees", id: `DEF:${args.recurringFeeId}` },
      ],
    }),

    listRecurringFeeDefinitions: builder.query<
      Connection<ConnectionItem<RecurringFeeDefinitionListResponse>>,
      RecurringFeeDefinitionListArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listRecurringFeeDefinitions", {
          schoolId: toText(queryArgs?.schoolId),
          isActive: queryArgs?.isActive,
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listRecurringFeeDefinitions",
            compact({
              schoolId: toText(args?.schoolId),
              isActive: args?.isActive,
            }),
          );
          return {
            data: toConnection<
              ConnectionItem<RecurringFeeDefinitionListResponse>
            >(
              data as ConnectionLike<
                ConnectionItem<RecurringFeeDefinitionListResponse>
              >,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result, _error, args) => [
        {
          type: "TuitionRecurringFees",
          id: `LIST:${toText(args?.schoolId) ?? "DEFAULT"}`,
        },
        ...(result?.items ?? []).map((item) => ({
          type: "TuitionRecurringFees" as const,
          id: `DEF:${String(item.id ?? "")}`,
        })),
      ],
    }),

    listRecurringFeeAssignments: builder.query<
      Connection<ConnectionItem<RecurringFeeAssignmentListResponse>>,
      RecurringFeeAssignmentListArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listRecurringFeeAssignments", {
          tuitionAccountId: toText(queryArgs?.tuitionAccountId),
          studentId: toText(queryArgs?.studentId),
          recurringFeeId: toText(queryArgs?.recurringFeeId),
          isActive: queryArgs?.isActive,
          limit: queryArgs?.limit,
          nextToken: toText(queryArgs?.nextToken ?? undefined),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listRecurringFeeAssignments",
            compact({
              tuitionAccountId: toText(args?.tuitionAccountId),
              studentId: toText(args?.studentId),
              recurringFeeId: toText(args?.recurringFeeId),
              isActive: args?.isActive,
              limit: args?.limit,
              nextToken: toText(args?.nextToken ?? undefined),
            }),
          );
          return {
            data: toConnection<
              ConnectionItem<RecurringFeeAssignmentListResponse>
            >(
              data as ConnectionLike<
                ConnectionItem<RecurringFeeAssignmentListResponse>
              >,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result, _error, args) => [
        { type: "TuitionRecurringAssignments", id: "ALL" },
        ...(args?.tuitionAccountId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `FAMILY:${args.tuitionAccountId}`,
              },
            ]
          : []),
        ...(args?.studentId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `STUDENT:${args.studentId}`,
              },
            ]
          : []),
        ...(args?.recurringFeeId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `FEE:${args.recurringFeeId}`,
              },
            ]
          : []),
        ...(result?.items ?? []).map((item) => ({
          type: "TuitionRecurringAssignments" as const,
          id: `ASSIGN:${String(item.id ?? "")}`,
        })),
      ],
    }),

    previewBulkChargeSegment: builder.query<
      BulkChargeSegmentPreviewResponse,
      PreviewBulkChargeSegmentArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("previewBulkChargeSegment", {
          grades: normalizeStringArray(queryArgs?.grades),
          divisions: normalizeStringArray(queryArgs?.divisions),
          gender: queryArgs?.gender,
          rateCategories: normalizeStringArray(queryArgs?.rateCategories),
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "previewBulkChargeSegment",
            compact({
              grades: normalizeStringArray(args?.grades),
              divisions: normalizeStringArray(args?.divisions),
              gender: args?.gender,
              rateCategories: normalizeStringArray(args?.rateCategories),
            }),
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    archiveAdHocTuitionCharge: builder.mutation<
      DomainRouterMutationData<"archiveAdHocTuitionCharge">,
      ArchiveAdHocTuitionChargeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "archiveAdHocTuitionCharge",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        { type: "TuitionDashboard", id: `LEDGER:${args.tuitionAccountId}` },
        { type: "TuitionInvoices", id: `LIST:${args.tuitionAccountId}` },
      ],
    }),

    reverseTuitionPayment: builder.mutation<
      DomainRouterMutationData<"reverseTuitionPayment">,
      ReverseTuitionPaymentInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "reverseTuitionPayment",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        { type: "TuitionDashboard", id: `LEDGER:${args.tuitionAccountId}` },
      ],
    }),

    createAdHocCharge: builder.mutation<
      DomainRouterMutationData<"createAdHocCharge">,
      CreateAdHocChargeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate("createAdHocCharge", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        { type: "TuitionDashboard", id: `LEDGER:${args.tuitionAccountId}` },
        { type: "TuitionInvoices", id: `LIST:${args.tuitionAccountId}` },
      ],
    }),

    createBulkCharge: builder.mutation<
      DomainRouterMutationData<"createBulkCharge">,
      CreateBulkChargeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate("createBulkCharge", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (result) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        ...(result?.id
          ? [
              {
                type: "TuitionBulkJobs" as const,
                id: `JOB:${String(result.id)}`,
              },
            ]
          : []),
      ],
    }),

    createRecurringFeeDefinition: builder.mutation<
      DomainRouterMutationData<"createRecurringFeeDefinition">,
      CreateRecurringFeeDefinitionInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "createRecurringFeeDefinition",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (result) => [
        { type: "TuitionRecurringFees", id: "LIST:DEFAULT" },
        ...(result?.schoolId
          ? [
              {
                type: "TuitionRecurringFees" as const,
                id: `LIST:${String(result.schoolId)}`,
              },
            ]
          : []),
      ],
    }),

    updateRecurringFeeDefinition: builder.mutation<
      DomainRouterMutationData<"updateRecurringFeeDefinition">,
      UpdateRecurringFeeDefinitionInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateRecurringFeeDefinition",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (result, _error, args) => [
        { type: "TuitionRecurringFees", id: `DEF:${args.recurringFeeId}` },
        { type: "TuitionRecurringFees", id: "LIST:DEFAULT" },
        ...(result?.schoolId
          ? [
              {
                type: "TuitionRecurringFees" as const,
                id: `LIST:${String(result.schoolId)}`,
              },
            ]
          : []),
        { type: "TuitionRecurringAssignments", id: "ALL" },
      ],
    }),

    deleteRecurringFeeDefinition: builder.mutation<
      DomainRouterMutationData<"deleteRecurringFeeDefinition">,
      DeleteRecurringFeeDefinitionInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "deleteRecurringFeeDefinition",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (result, _error, args) => [
        { type: "TuitionRecurringFees", id: `DEF:${args.recurringFeeId}` },
        { type: "TuitionRecurringFees", id: "LIST:DEFAULT" },
        ...(result?.schoolId
          ? [
              {
                type: "TuitionRecurringFees" as const,
                id: `LIST:${String(result.schoolId)}`,
              },
            ]
          : []),
        { type: "TuitionRecurringAssignments", id: "ALL" },
      ],
    }),

    assignRecurringFee: builder.mutation<
      DomainRouterMutationData<"assignRecurringFee">,
      AssignRecurringFeeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate("assignRecurringFee", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionRecurringAssignments", id: "ALL" },
        {
          type: "TuitionRecurringAssignments",
          id: `FAMILY:${args.tuitionAccountId}`,
        },
        {
          type: "TuitionRecurringAssignments",
          id: `STUDENT:${args.studentId}`,
        },
        {
          type: "TuitionRecurringAssignments",
          id: `FEE:${args.recurringFeeId}`,
        },
      ],
    }),

    unassignRecurringFee: builder.mutation<
      DomainRouterMutationData<"unassignRecurringFee">,
      UnassignRecurringFeeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate("unassignRecurringFee", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (result, _error, args) => [
        { type: "TuitionRecurringAssignments", id: "ALL" },
        {
          type: "TuitionRecurringAssignments",
          id: `ASSIGN:${args.assignmentId}`,
        },
        ...(result?.tuitionAccountId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `FAMILY:${String(result.tuitionAccountId)}`,
              },
            ]
          : []),
        ...(result?.studentId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `STUDENT:${String(result.studentId)}`,
              },
            ]
          : []),
        ...(result?.recurringFeeId
          ? [
              {
                type: "TuitionRecurringAssignments" as const,
                id: `FEE:${String(result.recurringFeeId)}`,
              },
            ]
          : []),
      ],
    }),

    bulkAssignRecurringFee: builder.mutation<
      DomainRouterMutationData<"bulkAssignRecurringFee">,
      BulkAssignRecurringFeeInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate(
            "bulkAssignRecurringFee",
            { input: args },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionRecurringAssignments", id: "ALL" },
        {
          type: "TuitionRecurringAssignments",
          id: `FEE:${args.recurringFeeId}`,
        },
      ],
    }),

    issueTuitionRefund: builder.mutation<
      DomainRouterMutationData<"issueTuitionRefund">,
      IssueTuitionRefundInput
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.mutate("issueTuitionRefund", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "TuitionDashboard", id: "SCHOOL_TREE" },
        { type: "TuitionDashboard", id: `LEDGER:${args.tuitionAccountId}` },
      ],
    }),

    getApplicantApplications: builder.query<
      AdmissionsApplicationConnection,
      ApplicantApplicationsArgs
    >({
      queryFn: async (args) => {
        try {
          const applicantId = toText(args.applicantId) ?? "";
          const data = await domainRouterClient.query(
            "getApplicantApplications",
            {
              applicantId,
              limit: args.limit,
              nextToken: args.nextToken,
            },
          );
          return {
            data: toConnection<AdmissionsApplication>(
              data as ConnectionLike<AdmissionsApplication>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result, _error, args) => [
        {
          type: "AdmissionsApplications",
          id: applicantApplicationsTagId(args.applicantId),
        },
        ...(result?.items ?? []).map((item) => ({
          type: "AdmissionsApplication" as const,
          id: String(item.id ?? ""),
        })),
      ],
    }),

    getParentAdmissionsDraft: builder.query<
      ParentAdmissionsDraftEnvelope,
      ParentAdmissionsDraftQueryArgs | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getParentAdmissionsDraft",
            args ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "ParentAdmissionsDraft", id: "ACTIVE" }],
    }),

    upsertParentAdmissionsDraft: builder.mutation<
      ParentAdmissionsDraft,
      UpsertParentAdmissionsDraftInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "upsertParentAdmissionsDraft",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "ParentAdmissionsDraft", id: "ACTIVE" }],
    }),

    deleteParentAdmissionsDraft: builder.mutation<
      boolean,
      DeleteParentAdmissionsDraftInput | void
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "deleteParentAdmissionsDraft",
            input ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "ParentAdmissionsDraft", id: "ACTIVE" }],
    }),

    submitAdmissionsApplication: builder.mutation<
      DomainRouterMutationData<"submitApplication">,
      SubmitAdmissionsApplicationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "submitApplication",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: () => [{ type: "AdmissionsApplications" }],
    }),

    appendEnrollmentToApplication: builder.mutation<
      DomainRouterMutationData<"updateApplication">,
      AppendEnrollmentApplicationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("updateApplication", {
            applicationId: input.applicationId,
            updates: input.updates,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    updateAdmissionsApplication: builder.mutation<
      DomainRouterMutationData<"updateApplication">,
      UpdateAdmissionsApplicationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("updateApplication", {
            applicationId: input.applicationId,
            updates: input.updates,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications" },
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    updateAdmissionsEnrollmentProgress: builder.mutation<
      DomainRouterMutationData<"updateEnrollmentProgress">,
      UpdateEnrollmentProgressInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateEnrollmentProgress",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: () => [{ type: "AdmissionsApplications" }],
    }),

    updateAdmissionsMedicalRecord: builder.mutation<
      DomainRouterMutationData<"updateMedicalRecord">,
      UpdateMedicalRecordInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateMedicalRecord",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: () => [{ type: "AdmissionsApplications" }],
    }),

    upsertEnrollmentStepData: builder.mutation<
      DomainRouterMutationData<"upsertEnrollmentStepData">,
      UpsertEnrollmentStepDataInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "upsertEnrollmentStepData",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: () => [{ type: "AdmissionsApplications" }],
    }),

    getAdmissionsApplication: builder.query<
      DomainRouterQueryData<"getApplication">,
      string
    >({
      queryFn: async (applicationId) => {
        try {
          const data = await domainRouterClient.query("getApplication", {
            id: applicationId,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, applicationId) => [
        { type: "AdmissionsApplication", id: applicationId },
      ],
    }),

    getAdmissionsDocuments: builder.query<
      AdmissionsDocumentConnection,
      AdmissionsDocumentsArgs
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getApplicationDocuments",
            {
              applicationId: args.applicationId,
              limit: args.limit,
              nextToken: args.nextToken,
            },
          );
          return {
            data: toConnection<AdmissionsDocument>(
              data as ConnectionLike<AdmissionsDocument>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "AdmissionsDocuments", id: args.applicationId },
      ],
    }),

    getAdmissionsTimeline: builder.query<
      AdmissionsTimelineConnection,
      AdmissionsTimelineArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getAdmissionsTimeline", {
          applicationId: queryArgs.applicationId,
        }),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("listTimelineEvents", {
            filter: JSON.stringify({ applicationId: args.applicationId }),
            limit: args.limit ?? 200,
            nextToken: args.nextToken,
          });
          return {
            data: toConnection<AdmissionsTimelineEvent>(
              data as ConnectionLike<AdmissionsTimelineEvent>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(args.applicationId),
        },
      ],
    }),

    addAdmissionsNote: builder.mutation<
      DomainRouterMutationData<"addNote">,
      AddAdmissionNoteInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("addNote", input);
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const optimisticId = `optimistic-note-${Date.now()}`;
        const occurredAtSeconds = Math.floor(Date.now() / 1000);
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "getAdmissionsTimeline",
            { applicationId: input.applicationId },
            (draft) => {
              draft.items.unshift({
                id: optimisticId,
                applicationId: input.applicationId,
                eventType: "note.added",
                summary: input.note?.content ?? "",
                occurredAt: occurredAtSeconds,
              } as AdmissionsTimelineEvent);
              draft.totalCount += 1;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
        {
          type: "AdmissionsApplication",
          id: input.applicationId,
        },
      ],
    }),

    updateAdmissionsNote: builder.mutation<
      DomainRouterMutationData<"updateNote">,
      UpdateAdmissionsNoteInput
    >({
      queryFn: async (input) => {
        try {
          const { applicationId: _applicationId, ...payload } = input;
          const data = await domainRouterClient.mutate("updateNote", payload);
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
        { type: "AdmissionsApplication", id: input.applicationId },
      ],
    }),

    deleteAdmissionsNote: builder.mutation<
      DomainRouterMutationData<"deleteNote">,
      DeleteAdmissionsNoteInput
    >({
      queryFn: async (input) => {
        try {
          const { applicationId: _applicationId, ...payload } = input;
          const data = await domainRouterClient.mutate("deleteNote", payload);
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
        { type: "AdmissionsApplication", id: input.applicationId },
      ],
    }),

    addAdmissionsDocument: builder.mutation<
      DomainRouterMutationData<"addDocument">,
      AddAdmissionsDocumentInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("addDocument", input);
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsDocuments", id: input.applicationId },
        { type: "AdmissionsApplication", id: input.applicationId },
      ],
    }),

    updateAdmissionsApplicationStatus: builder.mutation<
      DomainRouterMutationData<"updateApplicationStatus">,
      UpdateAdmissionsStatusInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateApplicationStatus",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          domainRouterApi.util.updateQueryData(
            "getAdmissionsApplication",
            input.applicationId,
            (draft) => {
              const record = asObject<DomainRecord>(
                draft as unknown as DomainRecord,
                {},
              );
              record.status = input.status;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications" },
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    scheduleAdmissionsInterview: builder.mutation<
      DomainRouterMutationData<"scheduleInterview">,
      ScheduleAdmissionsInterviewInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "scheduleInterview",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          domainRouterApi.util.updateQueryData(
            "getAdmissionsApplication",
            input.applicationId,
            (draft) => {
              const record = asObject<DomainRecord>(
                draft as unknown as DomainRecord,
                {},
              );
              record.interviewDate = input.interviewDate;
              record.interviewRequestStatus = "accepted";
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    requestAdmissionsInterviewSchedule: builder.mutation<
      DomainRouterMutationData<"requestInterviewSchedule">,
      RequestAdmissionsInterviewScheduleInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "requestInterviewSchedule",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const normalizedSlots = Array.isArray(input.preferredSlots)
          ? input.preferredSlots
              .filter((value) => Number.isFinite(value))
              .map((value) => Math.trunc(value))
              .sort((left, right) => left - right)
          : [];
        const notes = toText(input.notes) ?? null;
        const patch = dispatch(
          domainRouterApi.util.updateQueryData(
            "getAdmissionsApplication",
            input.applicationId,
            (draft) => {
              const record = asObject<DomainRecord>(
                draft as unknown as DomainRecord,
                {},
              );
              record.interviewRequestStatus = "pending";
              record.requestedInterviewSlots = normalizedSlots;
              record.interviewRequestNotes = notes;
              record.interviewRequestedAt = Math.floor(Date.now() / 1000);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    scheduleAdmissionsVisit: builder.mutation<
      DomainRouterMutationData<"scheduleVisit">,
      ScheduleAdmissionsVisitInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("scheduleVisit", input);
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "AdmissionsApplication", id: input.applicationId },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
      ],
    }),

    sendAdmissionsApplicationMessage: builder.mutation<
      DomainRouterMutationData<"sendApplicationMessage">,
      SendAdmissionsMessageInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "sendApplicationMessage",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
        { type: "AdmissionsApplication", id: input.applicationId },
      ],
    }),

    updateAdmissionsChildApplication: builder.mutation<
      DomainRouterMutationData<"updateChildApplication">,
      UpdateAdmissionsChildApplicationInput
    >({
      queryFn: async (input) => {
        try {
          const { applicationId: _applicationId, ...payload } = input;
          const data = await domainRouterClient.mutate(
            "updateChildApplication",
            payload,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "AdmissionsApplications", id: "LIST" },
        {
          type: "AdmissionsTimeline",
          id: admissionsTimelineTagId(input.applicationId),
        },
        { type: "AdmissionsApplication", id: input.applicationId },
      ],
    }),

    listFinancialAidApplicationsForReview: builder.query<
      FinancialAidReviewListResponse,
      FinancialAidDashboardArgs | void
    >({
      queryFn: async (args = {}) => {
        try {
          const data = await domainRouterClient.query(
            "listFinancialAidApplicationsForReview",
            {
              input: JSON.stringify(args),
            },
          );
          return {
            data: toConnection<FinancialAidReviewListItem>(
              data as ConnectionLike<FinancialAidReviewListItem>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result) => [
        { type: "FinancialAidReviews", id: "LIST" },
        ...(result?.items ?? []).map((item) => ({
          type: "FinancialAidReview" as const,
          id: String(item.id ?? ""),
        })),
      ],
    }),

    getFinancialAidApplicationForReview: builder.query<
      FinancialAidReviewDetail,
      string
    >({
      queryFn: async (applicationId) => {
        try {
          const data = await domainRouterClient.query(
            "getFinancialAidApplicationForReview",
            { applicationId },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, applicationId) => [
        { type: "FinancialAidReview", id: applicationId },
      ],
    }),

    getFinancialAidStatusHistory: builder.query<
      FinancialAidStatusHistoryResponse,
      string
    >({
      queryFn: async (applicationId) => {
        try {
          const data = await domainRouterClient.query(
            "getFinancialAidStatusHistory",
            { applicationId },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, applicationId) => [
        { type: "FinancialAidReview", id: applicationId },
      ],
    }),

    makeFinancialAidDecision: builder.mutation<
      DomainRouterMutationData<"makeFinancialAidDecision">,
      DomainRecord
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "makeFinancialAidDecision",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "FinancialAidReviews", id: "LIST" },
        { type: "AdmissionsApplications" },
        ...(toText(String(input?.applicationId ?? ""))
          ? [
              {
                type: "FinancialAidReview" as const,
                id: String(input.applicationId),
              },
            ]
          : []),
      ],
    }),

    updateFinancialAidApplication: builder.mutation<
      DomainRouterMutationData<"updateFinancialAidApplication">,
      DomainRecord
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateFinancialAidApplication",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => {
        const applicationId = toText(
          String(input?.applicationId ?? input?.id ?? ""),
        );
        return [
          { type: "FinancialAidReviews", id: "LIST" },
          ...(applicationId
            ? [
                {
                  type: "FinancialAidReview" as const,
                  id: applicationId,
                },
              ]
            : []),
        ];
      },
    }),

    requestFinancialAidMoreInfo: builder.mutation<
      DomainRouterMutationData<"requestFinancialAidMoreInfo">,
      DomainRecord
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "requestFinancialAidMoreInfo",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "FinancialAidReviews", id: "LIST" },
        ...(toText(String(input?.applicationId ?? ""))
          ? [
              {
                type: "FinancialAidReview" as const,
                id: String(input.applicationId),
              },
            ]
          : []),
      ],
    }),

    // INT-1128: administrator reopen of a decided financial-aid application.
    reopenFinancialAidApplication: builder.mutation<
      DomainRouterMutationData<"reopenFinancialAidApplication">,
      DomainRecord
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "reopenFinancialAidApplication",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "FinancialAidReviews", id: "LIST" },
        ...(toText(String(input?.applicationId ?? ""))
          ? [
              {
                type: "FinancialAidReview" as const,
                id: String(input.applicationId),
              },
            ]
          : []),
      ],
    }),

    generateFinancialAidDecisionLetter: builder.mutation<
      DomainRouterMutationData<"generateFinancialAidDecisionLetter">,
      DomainRecord
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "generateFinancialAidDecisionLetter",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "FinancialAidReviews", id: "LIST" },
        ...(toText(String(input?.applicationId ?? ""))
          ? [
              {
                type: "FinancialAidReview" as const,
                id: String(input.applicationId),
              },
            ]
          : []),
      ],
    }),

    getSettingsModuleConfig: builder.query<
      ModuleConfigResponse,
      ModuleConfigQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getSettingsModuleConfig",
          normalizeSettingsArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getModuleConfig",
            toSettingsQueryInput(args),
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "SettingsConfig", id: settingsConfigTagId(args) },
      ],
    }),

    listStaffUsers: builder.query<
      StaffUserListResponse,
      {
        search?: string;
        statusFilter?: "all" | "active" | "invited" | "deactivated";
        page?: number;
        limit?: number;
      } | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listStaffUsers", queryArgs ?? {}),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listStaffUsers",
            args ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({
                type: "StaffUsers" as const,
                id: String(item.userId),
              })),
              { type: "StaffUsers" as const, id: "LIST" },
            ]
          : [{ type: "StaffUsers" as const, id: "LIST" }],
    }),

    listPendingStaffRequests: builder.query<
      DomainRouterQueryData<"listPendingStaffRequests">,
      {
        status?: "all" | "pending" | "approved" | "rejected";
        schoolId?: string | null;
        orgId?: string | null;
        moduleKey?: string | null;
      } | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listPendingStaffRequests", queryArgs ?? {}),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listPendingStaffRequests",
            args ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({
                type: "PendingStaffRequests" as const,
                id: String(item.requestId),
              })),
              { type: "PendingStaffRequests" as const, id: "LIST" },
            ]
          : [{ type: "PendingStaffRequests" as const, id: "LIST" }],
    }),

    listModuleEnablementAuditEvents: builder.query<
      ModuleEnablementAuditEventsResponse,
      { schoolId?: string | null; limit?: number } | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listModuleEnablementAuditEvents", queryArgs ?? {}),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listModuleEnablementAuditEvents",
            args ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "ModuleGovernanceAudit", id: "LIST" }],
    }),

    inviteStaffUser: builder.mutation<
      DomainRouterMutationData<"inviteStaffUser">,
      {
        email: string;
        firstName: string;
        lastName: string;
        role:
          | "super-administrator"
          | "admin"
          | "principal"
          | "teacher"
          | "nurse"
          | "accounts";
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("inviteStaffUser", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "StaffUsers", id: "LIST" }],
    }),

    resendStaffInvite: builder.mutation<
      DomainRouterMutationData<"resendStaffInvite">,
      {
        userId: string;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("resendStaffInvite", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "StaffUsers", id: "LIST" }],
    }),

    updateStaffUserRole: builder.mutation<
      DomainRouterMutationData<"updateStaffUserRole">,
      {
        userId: string;
        role:
          | "super-administrator"
          | "admin"
          | "principal"
          | "teacher"
          | "nurse"
          | "accounts";
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("updateStaffUserRole", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "StaffUsers", id: String(input.userId) },
        { type: "StaffUsers", id: "LIST" },
      ],
    }),

    deactivateStaffUser: builder.mutation<
      DomainRouterMutationData<"deactivateStaffUser">,
      { userId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("deactivateStaffUser", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "StaffUsers", id: String(input.userId) },
        { type: "StaffUsers", id: "LIST" },
      ],
    }),

    deleteStaffUser: builder.mutation<
      DomainRouterMutationData<"deleteStaffUser">,
      {
        userId: string;
        reassignToUserId?: string | null;
        deleteWithoutAssigning?: boolean;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("deleteStaffUser", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "StaffUsers", id: String(input.userId) },
        { type: "StaffUsers", id: "LIST" },
        { type: "AdmissionsApplications", id: "LIST" },
        { type: "FinancialAidReviews", id: "LIST" },
      ],
    }),

    reactivateStaffUser: builder.mutation<
      DomainRouterMutationData<"reactivateStaffUser">,
      { userId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("reactivateStaffUser", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "StaffUsers", id: String(input.userId) },
        { type: "StaffUsers", id: "LIST" },
      ],
    }),

    approvePendingStaffRequest: builder.mutation<
      DomainRouterMutationData<"approvePendingStaffRequest">,
      {
        requestId: string;
        role?: "admin" | "principal" | "teacher" | "nurse" | "accounts";
        schoolId?: string | null;
        orgId?: string | null;
        moduleKey?: string | null;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "approvePendingStaffRequest",
            { input: JSON.stringify(input) },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "PendingStaffRequests", id: String(input.requestId) },
        { type: "PendingStaffRequests", id: "LIST" },
        { type: "StaffUsers", id: "LIST" },
      ],
    }),

    rejectPendingStaffRequest: builder.mutation<
      DomainRouterMutationData<"rejectPendingStaffRequest">,
      {
        requestId: string;
        schoolId?: string | null;
        orgId?: string | null;
        moduleKey?: string | null;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "rejectPendingStaffRequest",
            { input: JSON.stringify(input) },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "PendingStaffRequests", id: String(input.requestId) },
        { type: "PendingStaffRequests", id: "LIST" },
      ],
    }),

    getImpersonationSession: builder.query<ImpersonationSessionResponse, void>({
      serializeQueryArgs: () => buildCacheKey("getImpersonationSession", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "getImpersonationSession",
            {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "AdminImpersonationSession", id: "CURRENT" }],
    }),

    launchImpersonationSession: builder.mutation<
      DomainRouterMutationData<"launchImpersonationSession">,
      {
        effectiveUserId?: string;
        effectiveUserName?: string;
        effectiveUserEmail?: string;
        targetPersona?: "administrator" | "staff" | "parent";
        reason: string;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "launchImpersonationSession",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "AdminImpersonationSession", id: "CURRENT" }],
    }),

    clearImpersonationSession: builder.mutation<
      DomainRouterMutationData<"clearImpersonationSession">,
      void
    >({
      queryFn: async () => {
        try {
          const data = await domainRouterClient.mutate(
            "clearImpersonationSession",
            {
              input: JSON.stringify({}),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "AdminImpersonationSession", id: "CURRENT" }],
    }),

    getViewAsContext: builder.query<ViewAsContextResponse, void>({
      serializeQueryArgs: () => buildCacheKey("getViewAsContext", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query("getViewAsContext", {});
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "AdminViewAsContext", id: "CURRENT" }],
    }),

    setViewAsContext: builder.mutation<
      DomainRouterMutationData<"setViewAsContext">,
      { userId: string; userName: string; userEmail: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("setViewAsContext", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "AdminViewAsContext", id: "CURRENT" }],
    }),

    clearViewAsContext: builder.mutation<
      DomainRouterMutationData<"clearViewAsContext">,
      void
    >({
      queryFn: async () => {
        try {
          const data = await domainRouterClient.mutate("clearViewAsContext", {
            input: JSON.stringify({}),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "AdminViewAsContext", id: "CURRENT" }],
    }),

    listTenantSchools: builder.query<TenantSchoolsResponse, void>({
      serializeQueryArgs: () => buildCacheKey("listTenantSchools", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query("listTenantSchools", {});
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "TenantSchools", id: "LIST" }],
    }),

    getActiveSchoolContext: builder.query<ActiveSchoolContextResponse, void>({
      serializeQueryArgs: () => buildCacheKey("getActiveSchoolContext", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "getActiveSchoolContext",
            {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "ActiveSchoolContext", id: "CURRENT" }],
    }),

    switchSchoolContext: builder.mutation<
      DomainRouterMutationData<"switchSchoolContext">,
      { schoolId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("switchSchoolContext", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        { type: "ActiveSchoolContext", id: "CURRENT" },
        { type: "TenantSchools", id: "LIST" },
      ],
    }),

    getPaymentControlPlane: builder.query<
      PaymentControlPlaneResponse,
      PaymentControlPlaneQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getPaymentControlPlane",
          normalizePaymentControlPlaneArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getPaymentControlPlane",
            {
              input: JSON.stringify(normalizePaymentControlPlaneArgs(args)),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "PaymentControlPlane", id: paymentControlPlaneTagId(args) },
      ],
    }),

    getPaymentControlPlaneReadiness: builder.query<
      PaymentControlPlaneReadinessResponse,
      PaymentControlPlaneQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getPaymentControlPlaneReadiness",
          normalizePaymentControlPlaneArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getPaymentControlPlaneReadiness",
            {
              input: JSON.stringify(normalizePaymentControlPlaneArgs(args)),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "PaymentReadiness", id: paymentReadinessTagId(args) },
      ],
    }),

    getMyPaymentManagementContext: builder.query<
      PaymentManagementContextResponse,
      PaymentManagementContextQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getMyPaymentManagementContext",
          normalizePaymentManagementContextArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getMyPaymentManagementContext",
            {
              input: JSON.stringify(
                normalizePaymentManagementContextArgs(args),
              ),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "PaymentControlPlane",
          id: paymentManagementContextTagId(args),
        },
      ],
    }),

    listPaymentMirrorLedgerEntries: builder.query<
      PaymentMirrorLedgerListResponse,
      PaymentMirrorLedgerQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "listPaymentMirrorLedgerEntries",
          normalizePaymentMirrorLedgerArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listPaymentMirrorLedgerEntries",
            {
              input: JSON.stringify(normalizePaymentMirrorLedgerArgs(args)),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "PaymentControlPlane", id: paymentMirrorLedgerTagId(args) },
      ],
    }),

    updatePaymentControlPlane: builder.mutation<
      DomainRouterMutationData<"updatePaymentControlPlane">,
      UpdatePaymentControlPlaneInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updatePaymentControlPlane",
            {
              input,
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "PaymentControlPlane", id: paymentControlPlaneTagId(input) },
        { type: "PaymentReadiness", id: paymentReadinessTagId(input) },
      ],
    }),

    startStripeConnectedAccountOnboarding: builder.mutation<
      StripeConnectedAccountOnboardingResponse,
      StartStripeConnectedAccountOnboardingMutationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "startStripeConnectedAccountOnboarding",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "PaymentReadiness", id: paymentReadinessTagId(input) },
      ],
    }),

    completeStripeConnectedAccountOnboarding: builder.mutation<
      CompleteStripeConnectedAccountOnboardingResponse,
      CompleteStripeConnectedAccountOnboardingMutationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "completeStripeConnectedAccountOnboarding",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "PaymentControlPlane", id: paymentControlPlaneTagId(input) },
        { type: "PaymentReadiness", id: paymentReadinessTagId(input) },
      ],
    }),

    getAssistantSpreadsheetBridgeStatus: builder.query<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetBridgeStatusQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getAssistantSpreadsheetBridgeStatus",
          normalizeAssistantSpreadsheetBridgeArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getAssistantSpreadsheetBridgeStatus",
            {
              input: JSON.stringify(args ?? {}),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(args),
        },
      ],
    }),

    startAssistantSpreadsheetBridgeAuth: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetBridgeStartAuthInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "startAssistantSpreadsheetBridgeAuth",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    completeAssistantSpreadsheetBridgeAuth: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetBridgeOAuthCallbackInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "completeAssistantSpreadsheetBridgeAuth",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(),
        },
      ],
    }),

    exportAssistantSpreadsheetToGoogleDrive: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetBridgeExportInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "exportAssistantSpreadsheetToGoogleDrive",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    disconnectAssistantSpreadsheetBridge: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetBridgeDisconnectInput | void
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "disconnectAssistantSpreadsheetBridge",
            {
              input: JSON.stringify(input ?? {}),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    getAssistantSpreadsheetWorkbookDraftState: builder.query<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetWorkbookDraftStateQueryArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getAssistantSpreadsheetWorkbookDraftState",
          normalizeAssistantSpreadsheetDraftStateArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getAssistantSpreadsheetWorkbookDraftState",
            {
              input: JSON.stringify(args),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(args),
        },
      ],
    }),

    saveAssistantSpreadsheetWorkbookDraft: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetWorkbookDraftSaveInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "saveAssistantSpreadsheetWorkbookDraft",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    restoreAssistantSpreadsheetWorkbookDraft: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetWorkbookDraftRestoreInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "restoreAssistantSpreadsheetWorkbookDraft",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    saveAssistantSpreadsheetExportPreset: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetExportPresetSaveInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "saveAssistantSpreadsheetExportPreset",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    deleteAssistantSpreadsheetExportPreset: builder.mutation<
      AssistantSpreadsheetBridgeResponse,
      AssistantSpreadsheetExportPresetDeleteInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "deleteAssistantSpreadsheetExportPreset",
            {
              input: JSON.stringify(input),
            },
          );
          return { data: asObject<DomainRecord>(data, {}) };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "AssistantSpreadsheetBridge",
          id: assistantSpreadsheetBridgeTagId(input),
        },
      ],
    }),

    listAssistantWorkflowTemplates: builder.query<
      AssistantWorkflowTemplateListResponse,
      AssistantWorkflowTemplatesArgs | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listAssistantWorkflowTemplates",
            args ?? {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    getAssistantWorkflow: builder.query<
      AssistantWorkflowDefinitionResponse,
      AssistantWorkflowArgs
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getAssistantWorkflow",
            args,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    listAssistantWorkflowExecutions: builder.query<
      AssistantWorkflowExecutionListResponse,
      AssistantWorkflowExecutionsArgs
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listAssistantWorkflowExecutions",
            args,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    upsertAssistantWorkflowDraft: builder.mutation<
      AssistantWorkflowMutationResponse,
      UpsertAssistantWorkflowDraftArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "upsertAssistantWorkflowDraft",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    submitAssistantWorkflowForApproval: builder.mutation<
      AssistantWorkflowMutationResponse,
      SubmitAssistantWorkflowForApprovalArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "submitAssistantWorkflowForApproval",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    reviewAssistantWorkflow: builder.mutation<
      AssistantWorkflowMutationResponse,
      ReviewAssistantWorkflowArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "reviewAssistantWorkflow",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    mutateAssistantRun: builder.mutation<
      DomainRouterMutationData<"mutateAssistantRun">,
      MutateAssistantRunArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "mutateAssistantRun",
            input,
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    getFinancialAidHistoricalImportTemplate: builder.query<
      FinancialAidHistoricalImportTemplateResponse,
      FinancialAidHistoricalImportTemplateQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getFinancialAidHistoricalImportTemplate",
          normalizeSettingsArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getFinancialAidHistoricalImportTemplate",
            toSettingsQueryInput(args),
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "SettingsConfig", id: settingsConfigTagId(args) },
      ],
    }),

    getFinancialAidHistoricalImportStatus: builder.query<
      FinancialAidHistoricalImportStatusResponse,
      FinancialAidHistoricalImportStatusQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey(
          "getFinancialAidHistoricalImportStatus",
          normalizeHistoricalImportStatusArgs(queryArgs),
        ),
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getFinancialAidHistoricalImportStatus",
            toHistoricalImportStatusInput(args),
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "SettingsConfig", id: settingsConfigTagId(args) },
      ],
    }),

    updateSettingsModuleConfig: builder.mutation<
      DomainRouterMutationData<"updateModuleConfig">,
      UpdateModuleConfigInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("updateModuleConfig", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const cacheArgs: ModuleConfigQueryArgs = {
          orgId: input.orgId,
          moduleKey: input.moduleKey ?? DEFAULT_MODULE_KEY,
        };
        const changedTopLevelKeys = new Set(
          Object.keys(asObject(input.patch, {})),
        );
        const shouldRefreshFinancialAidDomain =
          changedTopLevelKeys.has("financialAid");
        const refreshFinancialAidDomain = () => {
          dispatch(
            domainRouterUniversalApi.util.invalidateTags([
              { type: "FinancialAidDomain", id: "LIST" },
            ]),
          );
        };
        try {
          const { data } = await queryFulfilled;
          dispatch(
            domainRouterApi.util.updateQueryData(
              "getSettingsModuleConfig",
              cacheArgs,
              (draft) => {
                Object.assign(
                  draft as DomainRecord,
                  asObject<DomainRecord>(data, {}),
                );
              },
            ),
          );
          if (shouldRefreshFinancialAidDomain) {
            refreshFinancialAidDomain();
          }
        } catch {
          if (shouldRefreshFinancialAidDomain) {
            refreshFinancialAidDomain();
          }
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SettingsConfig",
          id: settingsConfigTagId({
            orgId: input.orgId,
            moduleKey: input.moduleKey,
          }),
        },
        { type: "ModuleGovernanceAudit", id: "LIST" },
        { type: "PortalResources" },
      ],
    }),

    requestFinancialAidHistoricalImportUpload: builder.mutation<
      FinancialAidHistoricalImportUploadResponse,
      FinancialAidHistoricalImportUploadInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "requestFinancialAidHistoricalImportUpload",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SettingsConfig",
          id: settingsConfigTagId({
            orgId: input.orgId,
            moduleKey: input.moduleKey,
          }),
        },
      ],
    }),

    startFinancialAidHistoricalImport: builder.mutation<
      DomainRouterMutationData<"startFinancialAidHistoricalImport">,
      StartFinancialAidHistoricalImportInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "startFinancialAidHistoricalImport",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "SettingsConfig",
          id: settingsConfigTagId({
            orgId: input.orgId,
            moduleKey: input.moduleKey,
          }),
        },
      ],
    }),

    listPortalStudentProfiles: builder.query<
      PortalStudentProfileListResponse,
      PortalProfileQueryArgs | void
    >({
      queryFn: async (args = {}) => {
        try {
          const normalized = normalizePortalProfileArgs(args);
          const data = await domainRouterClient.query(
            "listPortalStudentProfiles",
            {
              input: JSON.stringify(compact(normalized)),
            },
          );
          return {
            data: toConnection<PortalStudentProfile>(
              data as ConnectionLike<PortalStudentProfile>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args = {}) => [
        {
          type: "PortalProfiles",
          id: portalArgsTagId(
            "PortalProfiles",
            normalizePortalProfileArgs(args),
          ),
        },
      ],
    }),

    listPortalDocuments: builder.query<
      PortalDocumentListResponse,
      PortalProfileQueryArgs | void
    >({
      queryFn: async (args = {}) => {
        try {
          const normalized = normalizePortalProfileArgs(args);
          const data = await domainRouterClient.query("listPortalDocuments", {
            input: JSON.stringify(compact(normalized)),
          });
          return {
            data: toConnection<PortalDocument>(
              data as ConnectionLike<PortalDocument>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args = {}) => [
        {
          type: "PortalDocuments",
          id: portalArgsTagId(
            "PortalDocuments",
            normalizePortalProfileArgs(args),
          ),
        },
      ],
    }),

    listPortalCalendarEvents: builder.query<
      PortalCalendarEvent[],
      PortalCalendarQueryArgs | void
    >({
      queryFn: async (args = {}) => {
        try {
          const normalized = normalizePortalCalendarArgs(args);
          const data = await domainRouterClient.query(
            "listPortalCalendarEvents",
            {
              input: JSON.stringify(compact(normalized)),
            },
          );
          return {
            data: toArray<PortalCalendarEvent>(
              data as readonly PortalCalendarEvent[],
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args = {}) => [
        {
          type: "PortalCalendar",
          id: portalArgsTagId(
            "PortalCalendar",
            normalizePortalCalendarArgs(args),
          ),
        },
      ],
    }),

    listPortalTransactions: builder.query<
      PortalTransactionListResponse,
      { familyId: string; academicYear?: string }
    >({
      queryFn: async (args) => {
        try {
          const familyId = toText(args.familyId) ?? "";
          const data = await domainRouterClient.query(
            "listPortalTransactions",
            {
              input: JSON.stringify(
                compact({ familyId, academicYear: toText(args.academicYear) }),
              ),
            },
          );
          return {
            data: toConnection<PortalTransaction>(
              data as ConnectionLike<PortalTransaction>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "PortalTransactions",
          id: portalTransactionsTagId(args.familyId, args.academicYear),
        },
      ],
    }),

    listPortalResources: builder.query<
      PortalResource[],
      PortalResourcesQueryArgs | void
    >({
      queryFn: async (args = {}) => {
        try {
          const normalized = normalizePortalResourcesArgs(args);
          const data = await domainRouterClient.query("listPortalResources", {
            input: JSON.stringify(compact(normalized)),
          });
          return {
            data: toArray<PortalResource>(data as readonly PortalResource[]),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args = {}) => [
        {
          type: "PortalResources",
          id: portalArgsTagId(
            "PortalResources",
            normalizePortalResourcesArgs(args),
          ),
        },
      ],
    }),

    getParentPortalFinancialAidStatus: builder.query<
      DomainRouterQueryData<"getFinancialAidParentStatusView">,
      { applicationId: string }
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getParentPortalFinancialAidStatus", {
          applicationId: toText(queryArgs.applicationId),
        }),
      queryFn: async (args) => {
        try {
          const applicationId = toText(args.applicationId) ?? "";
          const data = await domainRouterClient.query(
            "getFinancialAidParentStatusView",
            {
              applicationId,
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    listMessageThreads: builder.query<
      PortalThreadListResponse,
      PortalMessageThreadQueryArgs
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listMessageThreads", {
          ...normalizePortalThreadArgs(queryArgs),
        }),
      queryFn: async (args) => {
        try {
          const normalized = normalizePortalThreadArgs(args);
          const data = await domainRouterClient.query("listMessageThreads", {
            input: JSON.stringify(compact(normalized)),
          });
          return {
            data: toConnection<MessageThread>(
              data as ConnectionLike<MessageThread>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result, _error, args) => [
        {
          type: "MessageThreads",
          id: messageThreadsListTagId({
            familyId: args.familyId,
            participantId: args.participantId,
            applicationId: args.applicationId,
          }),
        },
        ...(result?.items ?? []).map((item) => ({
          type: "MessageThread" as const,
          id: String(item.id ?? ""),
        })),
      ],
    }),

    getMessageThread: builder.query<PortalThreadDetail, string>({
      queryFn: async (threadId) => {
        try {
          const data = await domainRouterClient.query("getMessageThread", {
            threadId,
          });
          const threadRaw = (data as { thread?: unknown }).thread;
          const messagesRaw = (data as { messages?: unknown }).messages;
          return {
            data: {
              thread: asObject(threadRaw, {}),
              messages: toArray(messagesRaw as readonly DomainRecord[]).map(
                (entry) => asObject(entry, {}),
              ),
            },
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, threadId) => [
        { type: "MessageThread", id: threadId },
      ],
    }),

    sendMessage: builder.mutation<
      DomainRouterMutationData<"sendMessage">,
      SendMessageInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("sendMessage", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const listKey: PortalMessageThreadQueryArgs = {
          familyId: input.familyId,
          participantId: input.senderId,
          applicationId: input.applicationId,
        };
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "listMessageThreads",
            listKey,
            (draft) => {
              draft.items.unshift({
                id: `optimistic-thread-${Date.now()}`,
                subject: input.subject ?? "Message",
                familyId: input.familyId ?? null,
                applicationId: input.applicationId ?? null,
                lastMessageAt: Date.now(),
                unreadCount: 1,
                status: "open",
              } as MessageThread);
              draft.totalCount += 1;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "MessageThreads",
          id: messageThreadsListTagId({
            familyId: input.familyId,
            participantId: input.senderId,
            applicationId: input.applicationId,
          }),
        },
      ],
    }),

    markMessageRead: builder.mutation<
      DomainRouterMutationData<"markMessageRead">,
      MarkMessageReadInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("markMessageRead", {
            input: JSON.stringify(input),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "getMessageThread",
            input.threadId,
            (draft) => {
              for (const message of draft.messages) {
                const messageRecord = message as DomainRecord;
                if (String(messageRecord.id ?? "") === input.messageId) {
                  messageRecord.readAt = input.readAt ?? Date.now();
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        { type: "MessageThread", id: input.threadId },
      ],
    }),

    listNotifications: builder.query<
      NotificationConnection,
      PortalNotificationQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listNotifications", {
          ...normalizePortalNotificationArgs(queryArgs),
        }),
      queryFn: async (args) => {
        try {
          const normalized = normalizePortalNotificationArgs(args);
          const transportArgs = stripNotificationActorId(normalized);
          const data = await domainRouterClient.query("listNotifications", {
            input: JSON.stringify(compact(transportArgs)),
          });
          return {
            data: toConnection<NotificationItem>(
              data as ConnectionLike<NotificationItem>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (result, _error, args) => {
        const normalized = normalizePortalNotificationArgs(args);
        return [
          {
            type: "Notifications",
            id: notificationsListTagId({
              actorId: normalized.actorId,
              familyId: normalized.familyId,
              includeRead: normalized.includeRead,
              modules: normalized.modules,
              categories: normalized.categories,
            }),
          },
          ...(result?.items ?? []).map((item) => ({
            type: "Notifications" as const,
            id: String(item.id ?? ""),
          })),
        ];
      },
    }),

    getNotification: builder.query<
      DomainRouterQueryData<"getNotification">,
      { id: string }
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getNotification", {
            id: args.id,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "Notifications", id: args.id },
      ],
    }),

    markNotificationRead: builder.mutation<
      DomainRouterMutationData<"markNotificationRead">,
      MarkNotificationReadInput & PortalNotificationQueryArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("markNotificationRead", {
            input: JSON.stringify({
              id: input.id,
              readAt: input.readAt,
            }),
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const listKey: PortalNotificationQueryArgs = {
          actorId: input.actorId,
          familyId: input.familyId,
          includeRead: input.includeRead ?? true,
          modules: input.modules,
          categories: input.categories,
        };
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "listNotifications",
            listKey,
            (draft) => {
              for (const item of draft.items) {
                const record = item as DomainRecord;
                if (String(record.id ?? "") === input.id) {
                  record.deliveryState = "read";
                  record.readAt = input.readAt ?? Date.now();
                }
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "Notifications",
          id: notificationsListTagId({
            actorId: input.actorId,
            familyId: input.familyId,
            includeRead: input.includeRead,
            modules: input.modules,
            categories: input.categories,
          }),
        },
      ],
    }),

    markAllNotificationsRead: builder.mutation<
      DomainRouterMutationData<"markAllNotificationsRead">,
      MarkAllNotificationsReadInput & PortalNotificationQueryArgs
    >({
      queryFn: async (input) => {
        try {
          const normalized = normalizePortalNotificationArgs(input);
          const transportArgs = stripNotificationActorId(normalized);
          const data = await domainRouterClient.mutate(
            "markAllNotificationsRead",
            {
              input: JSON.stringify({
                ...transportArgs,
                readAt: input.readAt,
              }),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const listKey: PortalNotificationQueryArgs = {
          actorId: input.actorId,
          familyId: input.familyId,
          includeRead: true,
          modules: input.modules,
          categories: input.categories,
        };
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "listNotifications",
            listKey,
            (draft) => {
              for (const item of draft.items) {
                const record = item as DomainRecord;
                record.deliveryState = "read";
                record.readAt = input.readAt ?? Date.now();
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "Notifications",
          id: notificationsListTagId({
            actorId: input.actorId,
            familyId: input.familyId,
            includeRead: true,
            modules: input.modules,
            categories: input.categories,
          }),
        },
      ],
    }),

    listNotificationPreferences: builder.query<
      NotificationPreferenceConnection,
      NotificationPreferencesQueryArgs | void
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("listNotificationPreferences", {
          ...normalizeNotificationPreferencesArgs(queryArgs),
        }),
      queryFn: async (args) => {
        try {
          const normalized = normalizeNotificationPreferencesArgs(args);
          const data = await domainRouterClient.query(
            "listNotificationPreferences",
            {
              input: JSON.stringify(compact(normalized)),
            },
          );
          return {
            data: toConnection<NotificationPreferenceItem>(
              data as ConnectionLike<NotificationPreferenceItem>,
            ),
          };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => {
        const normalized = normalizeNotificationPreferencesArgs(args);
        return [
          {
            type: "NotificationPreferences",
            id: notificationPreferencesTagId({
              actorId: normalized.actorId,
              role: normalized.role,
            }),
          },
        ];
      },
    }),

    updateNotificationPreferences: builder.mutation<
      DomainRouterMutationData<"updateNotificationPreferences">,
      UpdateNotificationPreferencesInput & PortalNotificationQueryArgs
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateNotificationPreferences",
            {
              input: JSON.stringify(input),
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const listKey = {
          actorId: input.actorId,
          role: input.role,
        };
        const patchResult = dispatch(
          domainRouterApi.util.updateQueryData(
            "listNotificationPreferences",
            listKey,
            (draft) => {
              if (!draft.items.length) {
                draft.items.push({
                  actorId: input.actorId ?? "",
                  role: input.role ?? "",
                  moduleFilters: input.moduleFilters ?? [],
                  categoryFilters: input.categoryFilters ?? [],
                  muted: input.muted ?? false,
                  digestFrequency: input.digestFrequency ?? "daily",
                  updatedAt: Date.now(),
                } as NotificationPreferenceItem);
                draft.totalCount = 1;
                return;
              }
              const item = draft.items[0] as DomainRecord;
              item.moduleFilters =
                input.moduleFilters ?? item.moduleFilters ?? [];
              item.categoryFilters =
                input.categoryFilters ?? item.categoryFilters ?? [];
              item.muted = input.muted ?? item.muted ?? false;
              item.digestFrequency =
                input.digestFrequency ?? item.digestFrequency ?? "daily";
              item.updatedAt = Date.now();
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            domainRouterApi.util.updateQueryData(
              "listNotificationPreferences",
              listKey,
              (draft) => {
                if (!draft.items.length) {
                  draft.items.push(data as NotificationPreferenceItem);
                  draft.totalCount = 1;
                  return;
                }
                draft.items[0] = {
                  ...(draft.items[0] as DomainRecord),
                  ...(data as DomainRecord),
                } as NotificationPreferenceItem;
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, input) => [
        {
          type: "NotificationPreferences",
          id: notificationPreferencesTagId({
            actorId: input.actorId,
            role: input.role,
          }),
        },
      ],
    }),

    // =========================================================================
    // Donations Domain Endpoints
    // =========================================================================

    getDonationsCampaignCalendar: builder.query<
      DomainRouterQueryData<"getDonationsCampaignCalendar">,
      {
        campaignId?: string;
        campaignType?: "general" | "annual-fund" | "parnes-hayom";
        year?: number;
        month?: number;
      } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getDonationsCampaignCalendar",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsCalendar"],
    }),

    getDonationsTenantConfig: builder.query<
      DomainRouterQueryData<"getDonationsTenantConfig">,
      void
    >({
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "getDonationsTenantConfig",
            {},
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsTenantConfig"],
    }),

    listDonationsFamilyHistory: builder.query<
      DomainRouterQueryData<"listDonationsFamilyHistory">,
      { limit?: number; nextToken?: string; campaignType?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listDonationsFamilyHistory",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsFamilyHistory"],
    }),

    listDonationsPendingDedications: builder.query<
      DomainRouterQueryData<"listDonationsPendingDedications">,
      { limit?: number; nextToken?: string; campaignId?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listDonationsPendingDedications",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsDedications"],
    }),

    getDonationsDailyHonorees: builder.query<
      DomainRouterQueryData<"getDonationsDailyHonorees">,
      { date?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getDonationsDailyHonorees",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsDedications"],
    }),

    getDonationsLedger: builder.query<
      DomainRouterQueryData<"getDonationsLedger">,
      {
        limit?: number;
        nextToken?: string;
        status?: string;
        campaignType?: string;
        startDate?: string;
        endDate?: string;
      } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getDonationsLedger", {
            input: args ?? {},
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsLedger"],
    }),

    getDonationsCampaignList: builder.query<
      DomainRouterQueryData<"getDonationsCampaignList">,
      { limit?: number; nextToken?: string; isActive?: boolean } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getDonationsCampaignList",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["DonationsCampaign"],
    }),

    getDonationsCampaignDetail: builder.query<
      DomainRouterQueryData<"getDonationsCampaignDetail">,
      { campaignId: string }
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getDonationsCampaignDetail",
            {
              input: args,
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "DonationsCampaign", id: args.campaignId },
      ],
    }),

    createDonationsCampaign: builder.mutation<
      DomainRouterMutationData<"createDonationsCampaign">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "createDonationsCampaign",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsCampaign"],
    }),

    updateDonationsCampaign: builder.mutation<
      DomainRouterMutationData<"updateDonationsCampaign">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateDonationsCampaign",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsCampaign", "DonationsCalendar"],
    }),

    approveDonationsDedication: builder.mutation<
      DomainRouterMutationData<"approveDonationsDedication">,
      { donationId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "approveDonationsDedication",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsDedications", "DonationsLedger"],
    }),

    rejectDonationsDedication: builder.mutation<
      DomainRouterMutationData<"rejectDonationsDedication">,
      { donationId: string; reason?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "rejectDonationsDedication",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsDedications", "DonationsLedger"],
    }),

    createDonationsOfflineDonation: builder.mutation<
      DomainRouterMutationData<"createDonationsOfflineDonation">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "createDonationsOfflineDonation",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsLedger", "DonationsCalendar"],
    }),

    issueDonationsRefund: builder.mutation<
      DomainRouterMutationData<"issueDonationsRefund">,
      { donationId: string; reason?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("issueDonationsRefund", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "DonationsLedger",
        "DonationsCalendar",
        "DonationsFamilyHistory",
      ],
    }),

    resendDonationsReceipt: builder.mutation<
      DomainRouterMutationData<"resendDonationsReceipt">,
      { donationId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "resendDonationsReceipt",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    updateDonationsTenantConfig: builder.mutation<
      DomainRouterMutationData<"updateDonationsTenantConfig">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateDonationsTenantConfig",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["DonationsTenantConfig"],
    }),

    // =========================================================================
    // Camps Domain Endpoints
    // =========================================================================

    getParentCatalog: builder.query<
      DomainRouterQueryData<"getParentCatalog">,
      void
    >({
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query("getParentCatalog", {});
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsParentCatalog"],
    }),

    getParentRegistration: builder.query<
      DomainRouterQueryData<"getParentRegistration">,
      { programId: string }
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getParentRegistration", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "CampsParentRegistration", id: args.programId },
      ],
    }),

    listParentWaitlist: builder.query<
      DomainRouterQueryData<"listParentWaitlist">,
      { limit?: number; nextToken?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("listParentWaitlist", {
            input: args ?? {},
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsParentWaitlist"],
    }),

    listParentRegistrations: builder.query<
      DomainRouterQueryData<"listParentRegistrations">,
      { limit?: number; nextToken?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listParentRegistrations",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsParentRegistrations"],
    }),

    getSchoolDashboard: builder.query<
      DomainRouterQueryData<"getSchoolDashboard">,
      void
    >({
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query("getSchoolDashboard", {});
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsSchoolDashboard"],
    }),

    getProgram: builder.query<
      DomainRouterQueryData<"getProgram">,
      { programId: string }
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getProgram", {
            input: args,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "CampsProgram", id: args.programId },
      ],
    }),

    getProgramEditor: builder.query<
      DomainRouterQueryData<"getProgramEditor">,
      { programId?: string } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query("getProgramEditor", {
            input: args ?? {},
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsProgramEditor"],
    }),

    listCampImportCrosswalkQueue: builder.query<
      DomainRouterQueryData<"listCampImportCrosswalkQueue">,
      {
        itemType?: "family" | "student" | "all";
        status?: "linked" | "ambiguous" | "orphaned" | "stale" | "unresolved";
        limit?: number;
        nextToken?: string;
      } | void
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "listCampImportCrosswalkQueue",
            {
              input: args ?? {},
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: ["CampsCrosswalkQueue"],
    }),

    getCampImportCrosswalkDetail: builder.query<
      DomainRouterQueryData<"getCampImportCrosswalkDetail">,
      { shadowFamilyId?: string; shadowStudentId?: string }
    >({
      queryFn: async (args) => {
        try {
          const data = await domainRouterClient.query(
            "getCampImportCrosswalkDetail",
            {
              input: args,
            },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: "CampsCrosswalkDetail",
          id: args.shadowStudentId ?? args.shadowFamilyId ?? "queue",
        },
      ],
    }),

    saveRegistrationDraft: builder.mutation<
      DomainRouterMutationData<"saveRegistrationDraft">,
      SaveRegistrationDraftInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "saveRegistrationDraft",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsParentRegistration"],
    }),

    submitRegistration: builder.mutation<
      DomainRouterMutationData<"submitRegistration">,
      SubmitRegistrationInput
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("submitRegistration", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsParentCatalog",
        "CampsParentRegistrations",
        "CampsParentRegistration",
        "CampsSchoolDashboard",
      ],
    }),

    cancelRegistration: builder.mutation<
      DomainRouterMutationData<"cancelRegistration">,
      { registrationId: string; reason?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("cancelRegistration", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsParentCatalog",
        "CampsParentRegistrations",
        "CampsSchoolDashboard",
        "CampsProgram",
      ],
    }),

    joinWaitlist: builder.mutation<
      DomainRouterMutationData<"joinWaitlist">,
      { programId: string; sessionId: string; studentId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("joinWaitlist", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsParentCatalog",
        "CampsParentWaitlist",
        "CampsSchoolDashboard",
      ],
    }),

    leaveWaitlist: builder.mutation<
      DomainRouterMutationData<"leaveWaitlist">,
      { waitlistEntryId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("leaveWaitlist", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsParentWaitlist", "CampsSchoolDashboard"],
    }),

    createProgram: builder.mutation<
      DomainRouterMutationData<"createProgram">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("createProgram", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsSchoolDashboard", "CampsProgramEditor"],
    }),

    updateProgram: builder.mutation<
      DomainRouterMutationData<"updateProgram">,
      Record<string, unknown>
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("updateProgram", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsSchoolDashboard",
        "CampsProgram",
        "CampsProgramEditor",
        "CampsParentCatalog",
      ],
    }),

    publishProgram: builder.mutation<
      DomainRouterMutationData<"publishProgram">,
      { programId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("publishProgram", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsSchoolDashboard",
        "CampsProgram",
        "CampsParentCatalog",
      ],
    }),

    archiveProgram: builder.mutation<
      DomainRouterMutationData<"archiveProgram">,
      { programId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("archiveProgram", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsSchoolDashboard",
        "CampsProgram",
        "CampsParentCatalog",
      ],
    }),

    updateSessionCapacity: builder.mutation<
      DomainRouterMutationData<"updateSessionCapacity">,
      { sessionId: string; capacity: number }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "updateSessionCapacity",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsSchoolDashboard",
        "CampsProgram",
        "CampsParentCatalog",
      ],
    }),

    promoteWaitlistEntry: builder.mutation<
      DomainRouterMutationData<"promoteWaitlistEntry">,
      { waitlistEntryId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("promoteWaitlistEntry", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsSchoolDashboard",
        "CampsProgram",
        "CampsParentWaitlist",
        "CampsParentCatalog",
      ],
    }),

    manualEnrollCamper: builder.mutation<
      DomainRouterMutationData<"manualEnrollCamper">,
      {
        programId: string;
        sessionId: string;
        studentId: string;
        waivePayment?: boolean;
        notes?: string;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("manualEnrollCamper", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsSchoolDashboard", "CampsProgram"],
    }),

    requestRosterExport: builder.mutation<
      DomainRouterMutationData<"requestRosterExport">,
      { programId: string; format: "csv" | "pdf" }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("requestRosterExport", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    issueRefund: builder.mutation<
      DomainRouterMutationData<"issueRefund">,
      { registrationId: string; amountCents?: number; reason?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate("issueRefund", {
            input,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [
        "CampsProgram",
        "CampsSchoolDashboard",
        "CampsParentRegistrations",
      ],
    }),

    triggerCampCommunication: builder.mutation<
      DomainRouterMutationData<"triggerCampCommunication">,
      {
        programId: string;
        audience: "registered" | "waitlist" | "all";
        templateKey: string;
      }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "triggerCampCommunication",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
    }),

    confirmCampShadowFamilyLink: builder.mutation<
      DomainRouterMutationData<"confirmCampShadowFamilyLink">,
      { shadowFamilyId: string; linkedFamilyId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "confirmCampShadowFamilyLink",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    confirmCampShadowStudentLink: builder.mutation<
      DomainRouterMutationData<"confirmCampShadowStudentLink">,
      { shadowStudentId: string; linkedStudentId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "confirmCampShadowStudentLink",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    rejectCampShadowFamilyCandidates: builder.mutation<
      DomainRouterMutationData<"rejectCampShadowFamilyCandidates">,
      { shadowFamilyId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "rejectCampShadowFamilyCandidates",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    rejectCampShadowStudentCandidates: builder.mutation<
      DomainRouterMutationData<"rejectCampShadowStudentCandidates">,
      { shadowStudentId: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "rejectCampShadowStudentCandidates",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    clearCampCrosswalkLink: builder.mutation<
      DomainRouterMutationData<"clearCampCrosswalkLink">,
      { shadowFamilyId?: string; shadowStudentId?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "clearCampCrosswalkLink",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    recomputeCampCrosswalkCandidates: builder.mutation<
      DomainRouterMutationData<"recomputeCampCrosswalkCandidates">,
      { shadowFamilyId?: string; shadowStudentId?: string }
    >({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "recomputeCampCrosswalkCandidates",
            { input },
          );
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: ["CampsCrosswalkQueue", "CampsCrosswalkDetail"],
    }),

    // ─── RTK Glue Seams: StaffSettings Governance ───────────────────────────
    // BoundedContext: StaffSettings · AggregateRoot: SettingsState
    // Backend connectors implemented by the external AppSync expert following
    // the single-head / multi-sub-API paradigm, RTK Glue verification gates,
    // and the YSTNJ domain model.

    listAdministrators: builder.query<DomainRecord, void>({
      serializeQueryArgs: () => buildCacheKey("listAdministrators", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "listAdministrators" as DomainRouterQueryName,
            {},
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "StaffAdministrators", id: "LIST" }],
    }),

    grantAdministrator: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "grantAdministrator" as DomainRouterMutationName,
            { input: JSON.stringify(input) },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "StaffAdministrators", id: "LIST" }],
    }),

    revokeAdministrator: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "revokeAdministrator" as DomainRouterMutationName,
            { input: JSON.stringify(input) },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "StaffAdministrators", id: "LIST" }],
    }),

    listStaffCodes: builder.query<DomainRecord, void>({
      serializeQueryArgs: () => buildCacheKey("listStaffCodes", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "listStaffCodes" as DomainRouterQueryName,
            {},
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "StaffCodes", id: "LIST" }],
    }),

    createStaffCode: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "createStaffCode" as DomainRouterMutationName,
            { input },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      // NOTE: fullValue returned once in response — never stored in cache.
      // Consuming component must hold fullValue in local useState only,
      // auto-masked after 60 s. Do not pass fullValue into RTK cache entries.
      invalidatesTags: [{ type: "StaffCodes", id: "LIST" }],
    }),

    rotateStaffCode: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "rotateStaffCode" as DomainRouterMutationName,
            { input },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      // NOTE: fullValue returned once in response — same security rule as createStaffCode.
      invalidatesTags: [{ type: "StaffCodes", id: "LIST" }],
    }),

    revokeStaffCode: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "revokeStaffCode" as DomainRouterMutationName,
            { input },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "StaffCodes", id: "LIST" }],
    }),

    listApprovedDomains: builder.query<DomainRecord, void>({
      serializeQueryArgs: () => buildCacheKey("listApprovedDomains", {}),
      queryFn: async () => {
        try {
          const data = await domainRouterClient.query(
            "listApprovedDomains" as DomainRouterQueryName,
            {},
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "ApprovedDomains", id: "LIST" }],
    }),

    addApprovedDomain: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "addApprovedDomain" as DomainRouterMutationName,
            { input },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "ApprovedDomains", id: "LIST" }],
    }),

    removeApprovedDomain: builder.mutation<DomainRecord, DomainRecord>({
      queryFn: async (input) => {
        try {
          const data = await domainRouterClient.mutate(
            "removeApprovedDomain" as DomainRouterMutationName,
            { input },
          );
          return { data: data as DomainRecord };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      invalidatesTags: [{ type: "ApprovedDomains", id: "LIST" }],
    }),

    // ─── RTK Glue Seam: Administrator Tuition Dashboard ─────────────────────
    // BoundedContext: Tuition · AggregateRoot: TuitionAccount
    // Wraps loadTuitionDashboardSnapshot so RTK manages the cache entry.
    // Future AppSync migration: swap queryFn body only; hook API stays identical.
    // Backend: external AppSync expert, single-head / multi-sub-API paradigm.

    getAdministratorTuitionDashboard: builder.query<
      TuitionDashboardSnapshot,
      { academicYear: string; familyLimit?: number }
    >({
      serializeQueryArgs: ({ queryArgs }) =>
        buildCacheKey("getAdministratorTuitionDashboard", queryArgs),
      queryFn: async (args) => {
        try {
          const data = await loadTuitionDashboardSnapshot(args.academicYear, {
            limit: args.familyLimit ?? 24,
          });
          return { data };
        } catch (error) {
          return { error: asError(error) };
        }
      },
      providesTags: [{ type: "TuitionDashboard", id: "ADMIN" }],
    }),
  }),
});

export const {
  useListAdmissionsApplicationsQuery,
  useListFacultyMembersQuery,
  useGetApplicantApplicationsQuery,
  useGetParentAdmissionsDraftQuery,
  useUpsertParentAdmissionsDraftMutation,
  useDeleteParentAdmissionsDraftMutation,
  useSubmitAdmissionsApplicationMutation,
  useAppendEnrollmentToApplicationMutation,
  useUpdateAdmissionsApplicationMutation,
  useUpdateAdmissionsEnrollmentProgressMutation,
  useUpdateAdmissionsMedicalRecordMutation,
  useUpsertEnrollmentStepDataMutation,
  useGetAdmissionsApplicationQuery,
  useGetAdmissionsDocumentsQuery,
  useGetAdmissionsTimelineQuery,
  useAddAdmissionsNoteMutation,
  useUpdateAdmissionsNoteMutation,
  useDeleteAdmissionsNoteMutation,
  useAddAdmissionsDocumentMutation,
  useUpdateAdmissionsApplicationStatusMutation,
  useRequestAdmissionsInterviewScheduleMutation,
  useScheduleAdmissionsInterviewMutation,
  useScheduleAdmissionsVisitMutation,
  useSendAdmissionsApplicationMessageMutation,
  useUpdateAdmissionsChildApplicationMutation,
  useListFinancialAidApplicationsForReviewQuery,
  useLazyListFinancialAidApplicationsForReviewQuery,
  useGetFinancialAidApplicationForReviewQuery,
  useGetFinancialAidStatusHistoryQuery,
  useMakeFinancialAidDecisionMutation,
  useUpdateFinancialAidApplicationMutation,
  useRequestFinancialAidMoreInfoMutation,
  useGenerateFinancialAidDecisionLetterMutation,
  useGetSettingsModuleConfigQuery,
  useGetPaymentControlPlaneQuery,
  useGetPaymentControlPlaneReadinessQuery,
  useGetMyPaymentManagementContextQuery,
  useListPaymentMirrorLedgerEntriesQuery,
  useGetAssistantSpreadsheetBridgeStatusQuery,
  useGetAssistantSpreadsheetWorkbookDraftStateQuery,
  useListAssistantWorkflowTemplatesQuery,
  useGetAssistantWorkflowQuery,
  useListAssistantWorkflowExecutionsQuery,
  useGetFinancialAidHistoricalImportTemplateQuery,
  useLazyGetFinancialAidHistoricalImportTemplateQuery,
  useGetFinancialAidHistoricalImportStatusQuery,
  useUpdateSettingsModuleConfigMutation,
  useUpdatePaymentControlPlaneMutation,
  useStartStripeConnectedAccountOnboardingMutation,
  useCompleteStripeConnectedAccountOnboardingMutation,
  useUpsertAssistantWorkflowDraftMutation,
  useSubmitAssistantWorkflowForApprovalMutation,
  useReviewAssistantWorkflowMutation,
  useMutateAssistantRunMutation,
  useStartAssistantSpreadsheetBridgeAuthMutation,
  useCompleteAssistantSpreadsheetBridgeAuthMutation,
  useExportAssistantSpreadsheetToGoogleDriveMutation,
  useDisconnectAssistantSpreadsheetBridgeMutation,
  useSaveAssistantSpreadsheetWorkbookDraftMutation,
  useRestoreAssistantSpreadsheetWorkbookDraftMutation,
  useSaveAssistantSpreadsheetExportPresetMutation,
  useDeleteAssistantSpreadsheetExportPresetMutation,
  useRequestFinancialAidHistoricalImportUploadMutation,
  useStartFinancialAidHistoricalImportMutation,
  useListPortalStudentProfilesQuery,
  useListPortalDocumentsQuery,
  useListPortalCalendarEventsQuery,
  useListPortalTransactionsQuery,
  useListPortalResourcesQuery,
  useGetParentPortalFinancialAidStatusQuery,
  useLazyGetParentPortalFinancialAidStatusQuery,
  useListMessageThreadsQuery,
  useGetMessageThreadQuery,
  useSendMessageMutation,
  useMarkMessageReadMutation,
  useListNotificationsQuery,
  useGetNotificationQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useListNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useListFamiliesQuery,
  useSearchFamiliesQuery,
  useGetFamilyBalanceQuery,
  useListFamilyAccountTransactionsQuery,
  useListFamilyPaymentMethodsQuery,
  useGetSchoolTuitionDashboardTreeQuery,
  useGetSchoolTuitionFamilyLedgerTreeQuery,
  useGetAdHocInvoiceQuery,
  useListAdHocInvoicesQuery,
  useGetBulkChargeJobQuery,
  useLazyGetBulkChargeJobQuery,
  usePreviewBulkChargeSegmentQuery,
  useLazyPreviewBulkChargeSegmentQuery,
  useGetRecurringFeeDefinitionQuery,
  useListRecurringFeeDefinitionsQuery,
  useListRecurringFeeAssignmentsQuery,
  useArchiveAdHocTuitionChargeMutation,
  useReverseTuitionPaymentMutation,
  useCreateAdHocChargeMutation,
  useCreateBulkChargeMutation,
  useCreateRecurringFeeDefinitionMutation,
  useUpdateRecurringFeeDefinitionMutation,
  useDeleteRecurringFeeDefinitionMutation,
  useAssignRecurringFeeMutation,
  useUnassignRecurringFeeMutation,
  useBulkAssignRecurringFeeMutation,
  useIssueTuitionRefundMutation,
  useGetDonationsCampaignCalendarQuery,
  useGetDonationsTenantConfigQuery,
  useListDonationsFamilyHistoryQuery,
  useListDonationsPendingDedicationsQuery,
  useGetDonationsDailyHonoreesQuery,
  useGetDonationsLedgerQuery,
  useGetDonationsCampaignListQuery,
  useGetDonationsCampaignDetailQuery,
  useCreateDonationsCampaignMutation,
  useUpdateDonationsCampaignMutation,
  useApproveDonationsDedicationMutation,
  useRejectDonationsDedicationMutation,
  useCreateDonationsOfflineDonationMutation,
  useIssueDonationsRefundMutation,
  useResendDonationsReceiptMutation,
  useUpdateDonationsTenantConfigMutation,
  useGetParentCatalogQuery,
  useGetParentRegistrationQuery,
  useListParentWaitlistQuery,
  useListParentRegistrationsQuery,
  useGetSchoolDashboardQuery,
  useGetProgramQuery,
  useGetProgramEditorQuery,
  useListCampImportCrosswalkQueueQuery,
  useGetCampImportCrosswalkDetailQuery,
  useSaveRegistrationDraftMutation,
  useSubmitRegistrationMutation,
  useCancelRegistrationMutation,
  useJoinWaitlistMutation,
  useLeaveWaitlistMutation,
  useCreateProgramMutation,
  useUpdateProgramMutation,
  usePublishProgramMutation,
  useArchiveProgramMutation,
  useUpdateSessionCapacityMutation,
  usePromoteWaitlistEntryMutation,
  useManualEnrollCamperMutation,
  useRequestRosterExportMutation,
  useIssueRefundMutation,
  useTriggerCampCommunicationMutation,
  useConfirmCampShadowFamilyLinkMutation,
  useConfirmCampShadowStudentLinkMutation,
  useRejectCampShadowFamilyCandidatesMutation,
  useRejectCampShadowStudentCandidatesMutation,
  useClearCampCrosswalkLinkMutation,
  useRecomputeCampCrosswalkCandidatesMutation,
  // StaffSettings Governance seams
  useListAdministratorsQuery,
  useListStaffUsersQuery,
  useListModuleEnablementAuditEventsQuery,
  useGrantAdministratorMutation,
  useRevokeAdministratorMutation,
  useInviteStaffUserMutation,
  useResendStaffInviteMutation,
  useUpdateStaffUserRoleMutation,
  useDeactivateStaffUserMutation,
  useDeleteStaffUserMutation,
  useReactivateStaffUserMutation,
  useListPendingStaffRequestsQuery,
  useApprovePendingStaffRequestMutation,
  useRejectPendingStaffRequestMutation,
  useListStaffCodesQuery,
  useCreateStaffCodeMutation,
  useRotateStaffCodeMutation,
  useRevokeStaffCodeMutation,
  useListApprovedDomainsQuery,
  useAddApprovedDomainMutation,
  useRemoveApprovedDomainMutation,
  useGetImpersonationSessionQuery,
  useLaunchImpersonationSessionMutation,
  useClearImpersonationSessionMutation,
  useGetViewAsContextQuery,
  useSetViewAsContextMutation,
  useClearViewAsContextMutation,
  useListTenantSchoolsQuery,
  useGetActiveSchoolContextQuery,
  useSwitchSchoolContextMutation,
  // Administrator Tuition Dashboard
  useGetAdministratorTuitionDashboardQuery,
} = domainRouterApi;
